/* ═══════════════════════════════════════════════════════════════
   Project Dashboard Widgets — v2.14.17 redesign.

   Principles (per docs/DESIGN.md):

     1. Sizes are honest. A widget lists ONLY the sizes that render
        genuinely distinct, useful content (WidgetDef.sizes). Compact =
        one hero stat, standard = the working summary, expanded = a full
        panel with chart/table/inline edit. Sizes that would merely
        stretch are not offered.

     2. Deliverables stand alone (2026-07-20 divorce). A deliverable is
        name + due date + project — never phase-grouped, never phase-
        gated. The old combined "Schedule & Milestones" widget is split:
          - PhasesWidget       ("Project Phase" — stepper, timekeeping/
                                forecasting context only)
          - DeliverablesWidget ("Deliverables" — list; Gantt-style
                                timeline + full inline editor expanded)

     3. Drill-through everywhere. ProjectWidgetCtx carries an `onDrill`
        callback the host (Projects.tsx) wires up.

     4. Tokens only — type/spacing/radius from base.css. The single
        exception: numeric font sizes INSIDE recharts SVG props (tick/
        label), a third-party boundary that requires numbers.

   Permission-graceful: every $-gated widget renders a non-financial
   fallback when the user can't see money.
   ═══════════════════════════════════════════════════════════════ */

import React, { useEffect, useState, useMemo } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Tooltip,
  XAxis,
  YAxis,
  LineChart,
  Line,
  CartesianGrid,
  ReferenceLine,
  ComposedChart,
} from 'recharts';
import { X, Clock, Mail, Check, CheckCircle2, Star, ArrowLeftRight, Plus } from 'lucide-react';
import type {
  HourEntry,
  HourForecast,
  Project,
  ProjectAssignment,
  TeamMember,
  Invoice,
  BillingRole,
  Task,
  Deliverable,
  ProjectPhase,
} from '../../shared/types';
import type { ProjectBudget, DashboardView } from '../../shared/entities';
import { TimeService, addDays, mondayOf } from '../services/timeService';
import { ProjectBudgetService } from '../services/projectBudgetService';
import { InvoiceService } from '../services/invoiceService';
import { TaskService } from '../services/taskService';
import { hasPermission } from '../../shared/permissions';

// ─── Drill-through targets ─────────────────────────────────────────

export type DashboardDrill =
  | { kind: 'tab'; tab: 'specs' | 'dashboard' }
  | { kind: 'widget-focus'; widgetId: string }
  | { kind: 'invoice'; invoiceId: string }
  | { kind: 'invoice-list'; status?: 'overdue' | 'all' }
  | { kind: 'budget-editor'; phaseId?: string; billingRoleId?: string }
  | { kind: 'forecast-week'; weekStart: string }
  | { kind: 'time-entry-list'; weekStart?: string }
  | { kind: 'deliverable'; deliverableId: string }
  | { kind: 'phase'; phaseId: string }
  | { kind: 'team-member'; memberId: string }
  | { kind: 'task'; taskId: string }
  | { kind: 'new-task' };

type Size = 'compact' | 'standard' | 'expanded';

// ─── Shared formatters / colors ────────────────────────────────────

const fmtMoney = (n: number) =>
  '$' + (n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtHours = (n: number) =>
  (n || 0).toLocaleString(undefined, { maximumFractionDigits: 1 }) + 'h';
const fmtPct = (n: number) =>
  (n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 }) + '%';

const COLOR_OK = 'var(--ok)';
const COLOR_WARN = 'var(--color-warning)';
const COLOR_DANGER = 'var(--dg)';
const COLOR_INFO = 'var(--color-info)';
const COLOR_NEUTRAL = 'var(--color-text-subtle)';
// Deliberate chart-series color: distinguishes "forecast" from "actual" (COLOR_INFO) across
// every burn/forecast chart in this file. Uses the --color-smart token (purple) — a series
// identity color, not a severity/status token — per DESIGN.md's data-series-palette exception.
const COLOR_FORECAST = 'var(--color-smart)';

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function daysBetween(a: string, b: string): number {
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  return Math.floor((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86400000);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function shortWeek(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return `${MONTHS[dt.getUTCMonth()]} ${dt.getUTCDate()}`;
}

function shortDate(iso: string): string {
  const [, m, d] = iso.split('-').map(Number);
  return `${MONTHS[m - 1]} ${d}`;
}

function projectMemberRate(m: TeamMember | undefined, billingRoles: BillingRole[]): number {
  if (!m) return 0;
  const role = billingRoles.find((r) => r.id === m.billingRoleId);
  return m.hourlyRate ?? role?.defaultRate ?? 0;
}

// ─── Shared widget context ─────────────────────────────────────────

export interface ProjectWidgetCtx {
  project: Project;
  assignments: ProjectAssignment[];
  members: TeamMember[];
  billingRoles: BillingRole[];
  userPermissions: string[];
  /** Optional drill-through router. Widgets call this on click to ask
   *  the host page to route to a tab, dialog, or filtered list. */
  onDrill?: (target: DashboardDrill) => void;
  /** Project phases — page-level state kept live by the host's
   *  specforma-sync-complete handler (syncPhasesWithDefaults order). */
  phases?: ProjectPhase[];
  /** Deliverables for this project — page-level live state. */
  deliverables?: Deliverable[];
  /** Whether the current user may advance/set the project phase. */
  canSetPhase?: boolean;
  /** Advance the phase stepper (click = In Progress, click again =
   *  Complete). The host persists + refreshes page-level state. */
  onSetPhase?: (phaseName: string) => Promise<void> | void;
  /** Whether the current user may create/edit/delete deliverables. */
  canEditDeliverables?: boolean;
  /** Save (id='' creates) / delete a deliverable. Host persists and
   *  refreshes page-level state so every widget stays in sync. */
  onSaveDeliverable?: (d: Deliverable) => Promise<void>;
  onDeleteDeliverable?: (id: string) => Promise<void>;
}

// ─── Reusable subcomponents ────────────────────────────────────────

function ProgressBar({ pct, color, height = 8 }: { pct: number; color: string; height?: number }) {
  return (
    <div
      style={{
        height,
        background: 'var(--sa)',
        borderRadius: 'var(--radius-sm)',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <div
        style={{
          width: '100%',
          height: '100%',
          background: color,
          transform: `scaleX(${Math.min(100, pct) / 100})`,
          transformOrigin: 'left',
          transition: 'transform var(--duration-slow) var(--ease-out)',
        }}
      />
      {pct > 100 && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '100%',
            background:
              'repeating-linear-gradient(45deg, transparent 0 6px, color-mix(in srgb, var(--dg) 25%, transparent) 6px 10px)',
          }}
        />
      )}
    </div>
  );
}

function WidgetSkeleton({ lines }: { lines: number }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
        padding: 'var(--space-2)',
      }}
      aria-hidden="true"
    >
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          style={{
            height: i === 0 ? 24 : 14,
            background: 'var(--sa)',
            borderRadius: 'var(--radius-sm)',
            opacity: 0.4 + i * 0.1,
            animation: 'pulse 1.4s ease-in-out infinite',
          }}
        />
      ))}
    </div>
  );
}

function WidgetEmptyState({
  text,
  action,
  onAction,
}: {
  text: string;
  action?: string;
  onAction?: () => void;
}) {
  return (
    <div
      style={{
        padding: 'var(--space-2)',
        height: '100%',
        textAlign: 'center',
        color: 'var(--tm)',
        fontSize: 'var(--font-size-base)',
        lineHeight: 1.4,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-2)',
      }}
    >
      <div>{text}</div>
      {action && onAction && (
        <button
          onClick={onAction}
          style={{
            padding: 'var(--space-2) var(--space-4)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--ac)',
            background: 'transparent',
            color: 'var(--ac)',
            fontSize: 'var(--font-size-base)',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {action}
        </button>
      )}
    </div>
  );
}

/** Accessible clickable list row — hover highlight, keyboard reachable,
 *  ≥32px effective hit area (DESIGN.md §4 Fitts floor). */
function Row({
  onClick,
  children,
  ariaLabel,
  style,
}: {
  onClick?: () => void;
  children: React.ReactNode;
  ariaLabel?: string;
  style?: React.CSSProperties;
}) {
  const clickable = !!onClick;
  return (
    <div
      onClick={onClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={ariaLabel}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      onMouseEnter={(e) => {
        if (clickable) (e.currentTarget as HTMLDivElement).style.background = 'var(--sa)';
      }}
      onMouseLeave={(e) => {
        if (clickable) (e.currentTarget as HTMLDivElement).style.background = 'transparent';
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: 'var(--space-1) var(--space-2)',
        minHeight: 32,
        borderRadius: 'var(--radius-sm)',
        cursor: clickable ? 'pointer' : 'default',
        transition: 'background var(--duration-fast) var(--ease-out)',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ─── Compact / KPI tile shell ──────────────────────────────────────

function KpiTile({
  value,
  label,
  bar,
  color,
  onClick,
  ariaLabel,
}: {
  value: string;
  label: string;
  bar?: { pct: number; color: string };
  color?: string;
  onClick?: () => void;
  ariaLabel?: string;
}) {
  const isClickable = !!onClick;
  return (
    <div
      onClick={onClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-label={ariaLabel || `${value} ${label}`}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
        padding: 'var(--space-2) var(--space-1)',
        cursor: isClickable ? 'pointer' : 'default',
        height: '100%',
        justifyContent: 'center',
        borderRadius: 'var(--radius-sm)',
      }}
    >
      <div
        style={{
          fontSize: 'var(--font-size-2xl)',
          fontWeight: 700,
          color: color || 'var(--tx)',
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
      <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}>{label}</div>
      {bar && <ProgressBar pct={bar.pct} color={bar.color} />}
    </div>
  );
}

const subText: React.CSSProperties = { fontSize: 'var(--font-size-sm)', color: 'var(--tm)' };
// Number hierarchy is deliberately quiet (Notion/Linear register): ONE hero
// number per card at most; supporting stats a step down. Color only when it
// carries a status signal.
const bigNum: React.CSSProperties = {
  fontSize: 'var(--font-size-xl)',
  fontWeight: 700,
  color: 'var(--tx)',
  fontVariantNumeric: 'tabular-nums',
  lineHeight: 1.1,
};
const heroNum: React.CSSProperties = { ...bigNum, fontSize: 'var(--font-size-2xl)' };
// Stat rows cluster LEFT with fixed rhythm — never spread edge-to-edge to
// "use" the card width (that reads as filler, not information).
const statRow: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  columnGap: 'var(--space-7)',
  rowGap: 'var(--space-3)',
  alignItems: 'baseline',
};

// Recharts boundary: SVG text props take numbers, not CSS vars — these
// two literals are the file's ONLY sanctioned raw type sizes (§2 note).
const axisTick = { fontSize: 10, fill: 'var(--tm)' };
const chartTooltipStyle: React.CSSProperties = {
  background: 'var(--bg)',
  border: '1px solid var(--bd)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-base)',
};

// ═══════════════════════════════════════════════════════════════
// 1. BUDGET vs ACTUAL — HOURS
//    compact  = hero stat + burn bar
//    standard = working summary + projection callout
//    expanded = hero row, weekly burn chart, phase table, edit actions
// ═══════════════════════════════════════════════════════════════

export function BudgetVsActualHoursWidget({ ctx, size }: { ctx: ProjectWidgetCtx; size: Size }) {
  const [budgets, setBudgets] = useState<ProjectBudget[]>([]);
  const [entries, setEntries] = useState<HourEntry[]>([]);
  const [phases, setPhases] = useState<ProjectPhase[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const [bR, eR, pR] = await Promise.allSettled([
        ProjectBudgetService.listForProject(ctx.project.id),
        TimeService.listEntries(ctx.project.id),
        TimeService.syncPhasesWithDefaults(ctx.project.id),
      ]);
      if (!mounted) return;
      if (bR.status === 'fulfilled') setBudgets(bR.value);
      if (eR.status === 'fulfilled') setEntries(eR.value);
      if (pR.status === 'fulfilled') setPhases(pR.value);
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, [ctx.project.id]);

  const totalBudgetHours = useMemo(() => {
    const total = budgets.find((b) => !b.phaseId && !b.billingRoleId);
    if (total?.budgetHours) return total.budgetHours;
    return ProjectBudgetService.computeRollup(budgets).hours;
  }, [budgets]);

  const actualHours = useMemo(
    () => entries.reduce((s, e) => s + (Number(e.hours) || 0), 0),
    [entries]
  );

  const pct = totalBudgetHours > 0 ? (actualHours / totalBudgetHours) * 100 : 0;
  const remaining = Math.max(0, totalBudgetHours - actualHours);
  const overBy = Math.max(0, actualHours - totalBudgetHours);
  const color = pct > 100 ? COLOR_DANGER : pct > 90 ? COLOR_WARN : COLOR_OK;

  // Inline budget setter (first-run UX)
  const [showSetBudget, setShowSetBudget] = useState(false);
  const [hoursIn, setHoursIn] = useState('');
  const [savingBudget, setSavingBudget] = useState(false);

  async function saveBudget() {
    setSavingBudget(true);
    try {
      const existing = budgets.find((b) => !b.phaseId && !b.billingRoleId);
      const next: ProjectBudget = {
        id: existing?.id || '',
        projectId: ctx.project.id,
        budgetHours: hoursIn ? parseFloat(hoursIn) : existing?.budgetHours,
        budgetAmount: existing?.budgetAmount,
      };
      await ProjectBudgetService.save(next);
      setShowSetBudget(false);
      setHoursIn('');
      const fresh = await ProjectBudgetService.listForProject(ctx.project.id);
      setBudgets(fresh);
    } finally {
      setSavingBudget(false);
    }
  }

  // Per-phase hours actuals — expanded only
  const phaseRows = useMemo(() => {
    const byPhase = new Map<string, number>();
    for (const e of entries) {
      const k = e.phaseId || '';
      byPhase.set(k, (byPhase.get(k) || 0) + (Number(e.hours) || 0));
    }
    return phases
      .map((p) => {
        const phaseBudget = budgets.find((b) => b.phaseId === p.id && !b.billingRoleId);
        const actual = byPhase.get(p.id) || 0;
        const budget = phaseBudget?.budgetHours || 0;
        const phasePct = budget > 0 ? (actual / budget) * 100 : 0;
        return { id: p.id, name: p.name, status: p.status, actual, budget, pct: phasePct };
      })
      .filter((r) => r.actual > 0 || r.budget > 0)
      .sort((a, b) => b.actual - a.actual);
  }, [phases, entries, budgets]);

  // Weekly burn series for expanded mode
  const weeklySeries = useMemo(() => {
    const today = todayIso();
    const buckets: Record<string, number> = {};
    for (let w = 0; w < 12; w++) {
      const ws = mondayOf(addDays(today, -7 * (11 - w)));
      buckets[ws] = 0;
    }
    for (const e of entries) {
      const ws = mondayOf(e.date);
      if (ws in buckets) buckets[ws] += Number(e.hours) || 0;
    }
    return Object.entries(buckets).map(([weekStart, hours]) => ({
      label: shortWeek(weekStart),
      weekStart,
      hours,
    }));
  }, [entries]);

  // Projected exhaustion: at current pace (last 4 wks avg), when do we hit budget?
  const projection = useMemo(() => {
    if (totalBudgetHours <= 0) return null;
    const last4 = weeklySeries.slice(-4);
    const avgPerWeek = last4.reduce((s, w) => s + w.hours, 0) / 4;
    if (avgPerWeek <= 0 || actualHours >= totalBudgetHours) return null;
    const weeksToExhaust = (totalBudgetHours - actualHours) / avgPerWeek;
    const exhaustDate = addDays(todayIso(), Math.round(weeksToExhaust * 7));
    return { weeksToExhaust, exhaustDate, avgPerWeek };
  }, [actualHours, totalBudgetHours, weeklySeries]);

  if (loading) return <WidgetSkeleton lines={2} />;

  if (totalBudgetHours === 0 && !showSetBudget) {
    return (
      <WidgetEmptyState
        text="No hours budget set."
        action="+ Set budget"
        onAction={() => setShowSetBudget(true)}
      />
    );
  }
  if (showSetBudget) {
    return (
      <div
        style={{
          padding: 'var(--space-2)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)',
        }}
      >
        <label
          htmlFor="sf-budget-hours-input"
          style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)', fontWeight: 600 }}
        >
          Project total budget
        </label>
        <input
          id="sf-budget-hours-input"
          type="number"
          placeholder="800"
          value={hoursIn}
          onChange={(e) => setHoursIn(e.target.value)}
          style={inputStyle}
        />
        <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
          <button onClick={() => setShowSetBudget(false)} style={btnGhost}>
            Cancel
          </button>
          <button
            onClick={saveBudget}
            disabled={savingBudget || !hoursIn}
            style={btnPrimary(!hoursIn)}
          >
            {savingBudget ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    );
  }

  // ─── COMPACT ─────
  if (size === 'compact') {
    return (
      <KpiTile
        value={fmtHours(actualHours)}
        label={`of ${fmtHours(totalBudgetHours)} · ${fmtPct(pct)} used`}
        bar={{ pct, color }}
        color={color}
        onClick={() => ctx.onDrill?.({ kind: 'budget-editor' })}
        ariaLabel={`Hours budget: ${fmtHours(actualHours)} of ${fmtHours(totalBudgetHours)} used. Open budget editor`}
      />
    );
  }

  // ─── STANDARD ─────
  if (size === 'standard') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-3)',
          padding: 'var(--space-1)',
        }}
      >
        <div style={statRow}>
          <div>
            <div style={{ ...bigNum, color }}>{fmtHours(actualHours)}</div>
            <div style={subText}>logged of {fmtHours(totalBudgetHours)}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ ...bigNum, color }}>{fmtPct(pct)}</div>
            <div style={subText}>used</div>
          </div>
        </div>
        <ProgressBar pct={pct} color={color} />
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--tm)',
          }}
        >
          <span>
            {remaining > 0 ? `${fmtHours(remaining)} remaining` : `${fmtHours(overBy)} over`}
          </span>
          <button onClick={() => setShowSetBudget(true)} style={linkBtn}>
            Edit budget
          </button>
        </div>
        {projection && (
          <div style={calloutStyle(COLOR_INFO)}>
            At {fmtHours(projection.avgPerWeek)}/wk → exhaust on{' '}
            <strong style={{ color: 'var(--tx)' }}>{shortDate(projection.exhaustDate)}</strong>
          </div>
        )}
      </div>
    );
  }

  // ─── EXPANDED ─────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div style={statRow}>
        <div>
          <div style={{ ...heroNum, color }}>{fmtHours(actualHours)}</div>
          <div style={subText}>actual hours</div>
        </div>
        <div>
          <div style={heroNum}>{fmtHours(totalBudgetHours)}</div>
          <div style={subText}>budget</div>
        </div>
        <div>
          <div style={{ ...heroNum, color }}>{fmtPct(pct)}</div>
          <div style={subText}>
            {remaining > 0 ? `${fmtHours(remaining)} left` : `${fmtHours(overBy)} over`}
          </div>
        </div>
      </div>
      <ProgressBar pct={pct} color={color} height={10} />
      {projection && (
        <div
          style={{
            ...calloutStyle(COLOR_INFO),
            fontSize: 'var(--font-size-base)',
            color: 'var(--tx)',
          }}
        >
          Burning <strong>{fmtHours(projection.avgPerWeek)}/wk</strong> over the last 4 weeks. At
          this pace, budget exhausts on <strong>{shortDate(projection.exhaustDate)}</strong> (
          {Math.round(projection.weeksToExhaust)} wks).
        </div>
      )}

      {/* Weekly burn chart */}
      <div style={{ height: 130 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={weeklySeries}
            margin={{ top: 4, right: 8, bottom: 0, left: -20 }}
            onClick={(s: any) => {
              const ws = s?.activePayload?.[0]?.payload?.weekStart;
              if (ws) ctx.onDrill?.({ kind: 'time-entry-list', weekStart: ws });
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="var(--bd)" />
            <XAxis dataKey="label" tick={axisTick} />
            <YAxis tick={axisTick} />
            <Tooltip contentStyle={chartTooltipStyle} formatter={(v: any) => [`${v}h`, 'Hours']} />
            <Bar dataKey="hours" fill={COLOR_INFO} radius={[3, 3, 0, 0]} cursor="pointer" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Phase breakdown */}
      {phaseRows.length > 0 && (
        <div>
          <div style={sectionLabel}>By phase · click to edit budget</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
            {phaseRows.map((p) => {
              const pcolor =
                p.budget === 0
                  ? COLOR_NEUTRAL
                  : p.pct > 100
                    ? COLOR_DANGER
                    : p.pct > 90
                      ? COLOR_WARN
                      : COLOR_OK;
              return (
                <Row
                  key={p.id}
                  onClick={() => ctx.onDrill?.({ kind: 'budget-editor', phaseId: p.id })}
                  ariaLabel={`Edit ${p.name} budget`}
                >
                  <span
                    style={{
                      flex: 1,
                      fontSize: 'var(--font-size-base)',
                      color: 'var(--tx)',
                      fontWeight: 600,
                    }}
                  >
                    {p.name}
                  </span>
                  <span style={{ ...subText, width: 130, textAlign: 'right' }}>
                    {fmtHours(p.actual)} / {p.budget > 0 ? fmtHours(p.budget) : '—'}
                  </span>
                  <div style={{ width: 110 }}>
                    <ProgressBar pct={p.pct} color={pcolor} height={6} />
                  </div>
                  <span
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      color: pcolor,
                      width: 50,
                      textAlign: 'right',
                      fontWeight: 600,
                    }}
                  >
                    {p.budget > 0 ? fmtPct(p.pct) : '—'}
                  </span>
                </Row>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
        <button onClick={() => setShowSetBudget(true)} style={btnGhost}>
          Edit project total
        </button>
        <button onClick={() => ctx.onDrill?.({ kind: 'budget-editor' })} style={btnGhost}>
          Open budget editor
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// 2. BUDGET vs ACTUAL — DOLLARS  (with hours-only graceful fallback)
//    compact  = hero cost + burn bar
//    standard = summary + assigned count
//    expanded = hero row + phase cost table + budget editor action
// ═══════════════════════════════════════════════════════════════

export function BudgetVsActualDollarsWidget({ ctx, size }: { ctx: ProjectWidgetCtx; size: Size }) {
  const canSeeMoney = hasPermission(ctx.userPermissions, 'projects.view_financials');
  const [budgets, setBudgets] = useState<ProjectBudget[]>([]);
  const [entries, setEntries] = useState<HourEntry[]>([]);
  const [phases, setPhases] = useState<ProjectPhase[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const [bR, eR, pR] = await Promise.allSettled([
        ProjectBudgetService.listForProject(ctx.project.id),
        TimeService.listEntries(ctx.project.id),
        TimeService.syncPhasesWithDefaults(ctx.project.id),
      ]);
      if (!mounted) return;
      if (bR.status === 'fulfilled') setBudgets(bR.value);
      if (eR.status === 'fulfilled') setEntries(eR.value);
      if (pR.status === 'fulfilled') setPhases(pR.value);
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, [ctx.project.id]);

  const actualDollars = useMemo(() => {
    let sum = 0;
    for (const e of entries) {
      const member = ctx.members.find((m) => m.id === e.teamMemberId);
      sum += (Number(e.hours) || 0) * projectMemberRate(member, ctx.billingRoles);
    }
    return sum;
  }, [entries, ctx.members, ctx.billingRoles]);

  const totalBudgetDollars = useMemo(() => {
    const total = budgets.find((b) => !b.phaseId && !b.billingRoleId);
    if (total?.budgetAmount) return total.budgetAmount;
    return ProjectBudgetService.computeRollup(budgets).amount;
  }, [budgets]);

  // Phase breakdown for $ — must compute BEFORE early returns so
  // the hook order is stable across renders.
  const phaseRows = useMemo(() => {
    const byPhase = new Map<string, number>();
    for (const e of entries) {
      const m = ctx.members.find((x) => x.id === e.teamMemberId);
      const cost = (Number(e.hours) || 0) * projectMemberRate(m, ctx.billingRoles);
      const k = e.phaseId || '';
      byPhase.set(k, (byPhase.get(k) || 0) + cost);
    }
    return phases
      .map((p) => {
        const phaseBudget = budgets.find((b) => b.phaseId === p.id && !b.billingRoleId);
        const actual = byPhase.get(p.id) || 0;
        const budget = phaseBudget?.budgetAmount || 0;
        const phasePct = budget > 0 ? (actual / budget) * 100 : 0;
        return { id: p.id, name: p.name, actual, budget, pct: phasePct };
      })
      .filter((r) => r.actual > 0 || r.budget > 0)
      .sort((a, b) => b.actual - a.actual);
  }, [phases, entries, budgets, ctx.members, ctx.billingRoles]);

  const pct = totalBudgetDollars > 0 ? (actualDollars / totalBudgetDollars) * 100 : 0;
  const remaining = Math.max(0, totalBudgetDollars - actualDollars);
  const overBy = Math.max(0, actualDollars - totalBudgetDollars);
  const color = pct > 100 ? COLOR_DANGER : pct > 90 ? COLOR_WARN : COLOR_OK;

  if (loading) return <WidgetSkeleton lines={2} />;

  // ─── PERMISSION-GRACEFUL FALLBACK ─────
  // No view_financials? Show a hours-only summary of cost-driving entries
  // (count + total hours), without ever leaking a $ amount.
  if (!canSeeMoney) {
    const totalH = entries.reduce((s, e) => s + (Number(e.hours) || 0), 0);
    if (size === 'compact') {
      return (
        <KpiTile
          value={fmtHours(totalH)}
          label={`${entries.length} entries · cost detail hidden`}
        />
      );
    }
    return (
      <div style={{ padding: 'var(--space-1)' }}>
        <div style={bigNum}>{fmtHours(totalH)}</div>
        <div style={subText}>{entries.length} time entries logged.</div>
        <div style={{ ...calloutStyle(COLOR_NEUTRAL), marginTop: 'var(--space-3)' }}>
          Cost detail requires <code>projects.view_financials</code>.
        </div>
      </div>
    );
  }

  if (totalBudgetDollars === 0)
    return (
      <WidgetEmptyState
        text="No dollar budget set."
        action="Open budget editor"
        onAction={() => ctx.onDrill?.({ kind: 'budget-editor' })}
      />
    );

  if (size === 'compact') {
    return (
      <KpiTile
        value={fmtMoney(actualDollars)}
        label={`of ${fmtMoney(totalBudgetDollars)} · ${fmtPct(pct)} used`}
        bar={{ pct, color }}
        color={color}
        onClick={() => ctx.onDrill?.({ kind: 'budget-editor' })}
        ariaLabel={`Cost: ${fmtMoney(actualDollars)} of ${fmtMoney(totalBudgetDollars)} used. Open budget editor`}
      />
    );
  }

  if (size === 'standard') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-3)',
          padding: 'var(--space-1)',
        }}
      >
        <div style={statRow}>
          <div>
            <div style={{ ...bigNum, color }}>{fmtMoney(actualDollars)}</div>
            <div style={subText}>actual cost of {fmtMoney(totalBudgetDollars)}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ ...bigNum, color }}>{fmtPct(pct)}</div>
            <div style={subText}>burned</div>
          </div>
        </div>
        <ProgressBar pct={pct} color={color} />
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--tm)',
          }}
        >
          <span>
            {remaining > 0 ? `${fmtMoney(remaining)} remaining` : `${fmtMoney(overBy)} over budget`}
          </span>
          <span>{ctx.assignments.length} assigned</span>
        </div>
      </div>
    );
  }

  // EXPANDED
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div style={statRow}>
        <div>
          <div style={{ ...heroNum, color }}>{fmtMoney(actualDollars)}</div>
          <div style={subText}>actual cost</div>
        </div>
        <div>
          <div style={heroNum}>{fmtMoney(totalBudgetDollars)}</div>
          <div style={subText}>budget</div>
        </div>
        <div>
          <div style={{ ...heroNum, color }}>{fmtPct(pct)}</div>
          <div style={subText}>
            {remaining > 0 ? `${fmtMoney(remaining)} left` : `${fmtMoney(overBy)} over`}
          </div>
        </div>
      </div>
      <ProgressBar pct={pct} color={color} height={10} />

      {phaseRows.length > 0 && (
        <div>
          <div style={sectionLabel}>By phase</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
            {phaseRows.map((p) => {
              const pcolor =
                p.budget === 0
                  ? COLOR_NEUTRAL
                  : p.pct > 100
                    ? COLOR_DANGER
                    : p.pct > 90
                      ? COLOR_WARN
                      : COLOR_OK;
              return (
                <Row
                  key={p.id}
                  onClick={() => ctx.onDrill?.({ kind: 'budget-editor', phaseId: p.id })}
                  ariaLabel={`Edit ${p.name} budget`}
                >
                  <span
                    style={{
                      flex: 1,
                      fontSize: 'var(--font-size-base)',
                      color: 'var(--tx)',
                      fontWeight: 600,
                    }}
                  >
                    {p.name}
                  </span>
                  <span style={{ ...subText, width: 160, textAlign: 'right' }}>
                    {fmtMoney(p.actual)} / {p.budget > 0 ? fmtMoney(p.budget) : '—'}
                  </span>
                  <div style={{ width: 110 }}>
                    <ProgressBar pct={p.pct} color={pcolor} height={6} />
                  </div>
                  <span
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      color: pcolor,
                      width: 50,
                      textAlign: 'right',
                      fontWeight: 600,
                    }}
                  >
                    {p.budget > 0 ? fmtPct(p.pct) : '—'}
                  </span>
                </Row>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={() => ctx.onDrill?.({ kind: 'budget-editor' })} style={btnGhost}>
          Open budget editor
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// 3. FORECAST OUTLOOK — next 4 weeks
//    compact  = hero total
//    standard = total + weekly chart
//    expanded = stats + chart + per-person rollup
// ═══════════════════════════════════════════════════════════════

export function ForecastOutlookWidget({ ctx, size }: { ctx: ProjectWidgetCtx; size: Size }) {
  const [forecasts, setForecasts] = useState<HourForecast[]>([]);
  const [loading, setLoading] = useState(true);

  const start = useMemo(() => addDays(mondayOf(todayIso()), 7), []);
  const end = useMemo(() => addDays(start, 28), [start]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const fcs = await TimeService.listForecastsForRange(start, end);
      if (!mounted) return;
      setForecasts(fcs.filter((f) => f.projectId === ctx.project.id));
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, [ctx.project.id, start, end]);

  const weekData = useMemo(() => {
    const buckets: Record<string, { total: number; perMember: Map<string, number> }> = {};
    for (let w = 0; w < 4; w++) {
      const ws = addDays(start, w * 7);
      buckets[ws] = { total: 0, perMember: new Map() };
    }
    for (const f of forecasts) {
      const ws = mondayOf(f.dayDate || f.weekStartDate);
      if (!(ws in buckets)) continue;
      const h = Number(f.hours) || 0;
      buckets[ws].total += h;
      buckets[ws].perMember.set(
        f.teamMemberId,
        (buckets[ws].perMember.get(f.teamMemberId) || 0) + h
      );
    }
    return Object.entries(buckets).map(([weekStart, d]) => ({
      label: shortWeek(weekStart),
      weekStart,
      hours: d.total,
      perMember: d.perMember,
    }));
  }, [forecasts, start]);

  const totalForecastHours = weekData.reduce((s, d) => s + d.hours, 0);

  // Per-member rollup over 4 wks
  const memberRollup = useMemo(() => {
    const byMember = new Map<string, number>();
    for (const f of forecasts) {
      byMember.set(f.teamMemberId, (byMember.get(f.teamMemberId) || 0) + (Number(f.hours) || 0));
    }
    return Array.from(byMember.entries())
      .map(([id, hours]) => ({
        id,
        name: ctx.members.find((m) => m.id === id)?.name || 'Unknown',
        hours,
      }))
      .sort((a, b) => b.hours - a.hours);
  }, [forecasts, ctx.members]);

  if (loading) return <WidgetSkeleton lines={2} />;

  if (size === 'compact') {
    return (
      <KpiTile
        value={fmtHours(totalForecastHours)}
        label="forecast · next 4 wks"
        onClick={() => ctx.onDrill?.({ kind: 'forecast-week', weekStart: start })}
        ariaLabel={`${fmtHours(totalForecastHours)} forecast next 4 weeks. Open forecasting`}
      />
    );
  }

  const chart = (height: number | '100%') => (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={weekData}
        margin={{ top: 8, right: 8, bottom: 0, left: -20 }}
        onClick={(s: any) => {
          const ws = s?.activePayload?.[0]?.payload?.weekStart;
          if (ws) ctx.onDrill?.({ kind: 'forecast-week', weekStart: ws });
        }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="var(--bd)" />
        <XAxis dataKey="label" tick={axisTick} />
        <YAxis tick={axisTick} />
        <Tooltip contentStyle={chartTooltipStyle} formatter={(v: any) => [`${v}h`, 'Forecast']} />
        <Bar dataKey="hours" fill={COLOR_FORECAST} radius={[3, 3, 0, 0]} cursor="pointer" />
      </BarChart>
    </ResponsiveContainer>
  );

  if (size === 'standard') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)',
          padding: 'var(--space-1)',
          height: '100%',
        }}
      >
        <div style={statRow}>
          <div>
            <div style={bigNum}>{fmtHours(totalForecastHours)}</div>
            <div style={subText}>forecasted next 4 weeks</div>
          </div>
          <div style={subText}>
            {memberRollup.length} {memberRollup.length === 1 ? 'person' : 'people'}
          </div>
        </div>
        <div style={{ flex: 1, minHeight: 100 }}>{chart('100%')}</div>
      </div>
    );
  }

  // EXPANDED: chart + per-member breakdown
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div style={statRow}>
        <div>
          <div style={heroNum}>{fmtHours(totalForecastHours)}</div>
          <div style={subText}>forecasted next 4 weeks</div>
        </div>
        <div>
          <div style={heroNum}>{memberRollup.length}</div>
          <div style={subText}>people forecasted</div>
        </div>
      </div>
      <div style={{ height: 160 }}>{chart(160)}</div>
      {memberRollup.length > 0 && (
        <div>
          <div style={sectionLabel}>By person · 4-week total</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
            {memberRollup.map((m) => (
              <Row
                key={m.id}
                onClick={() => ctx.onDrill?.({ kind: 'team-member', memberId: m.id })}
                ariaLabel={`View ${m.name}`}
              >
                <span style={{ flex: 1, fontSize: 'var(--font-size-base)', color: 'var(--tx)' }}>
                  {m.name}
                </span>
                <span
                  style={{
                    ...subText,
                    fontFamily: 'var(--font-mono)',
                    width: 60,
                    textAlign: 'right',
                  }}
                >
                  {fmtHours(m.hours)}
                </span>
              </Row>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// 4. AR AGING — with drill-through invoice list
//    compact  = hero outstanding total
//    standard = total + age buckets
//    expanded = stats + buckets + open-invoice list
// ═══════════════════════════════════════════════════════════════

export function ARagingWidget({ ctx, size }: { ctx: ProjectWidgetCtx; size: Size }) {
  const canSeeMoney = hasPermission(ctx.userPermissions, 'invoices.view_financials');
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const all = await InvoiceService.list(ctx.project.id);
        if (!mounted) return;
        setInvoices(all);
      } catch {
        // Invoice table may be empty / not rebuilt yet
      }
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, [ctx.project.id]);

  const { buckets, perBucketInvoices } = useMemo(() => {
    const today = new Date(todayIso());
    const buckets = { current: 0, d30: 0, d60: 0, d90: 0 };
    const perBucketInvoices: Record<'current' | 'd30' | 'd60' | 'd90', Invoice[]> = {
      current: [],
      d30: [],
      d60: [],
      d90: [],
    };
    for (const inv of invoices) {
      const status = (inv.status || '') as string;
      if (!status || status === 'draft' || status === 'paid' || status === 'void') continue;
      const issued = (inv as any).issueDate || (inv as any).date;
      if (!issued) continue;
      const days = Math.floor((today.getTime() - new Date(issued).getTime()) / 86400000);
      const open = (inv.total || 0) - ((inv as any).amountPaid || 0);
      if (open <= 0) continue;
      let bucket: 'current' | 'd30' | 'd60' | 'd90';
      if (days <= 30) bucket = 'current';
      else if (days <= 60) bucket = 'd30';
      else if (days <= 90) bucket = 'd60';
      else bucket = 'd90';
      buckets[bucket] += open;
      perBucketInvoices[bucket].push(inv);
    }
    return { buckets, perBucketInvoices };
  }, [invoices]);

  const total = buckets.current + buckets.d30 + buckets.d60 + buckets.d90;
  const overdueCount =
    perBucketInvoices.d30.length + perBucketInvoices.d60.length + perBucketInvoices.d90.length;

  if (loading) return <WidgetSkeleton lines={2} />;

  // Permission-graceful fallback: show invoice COUNT, not $
  if (!canSeeMoney) {
    const openCount = invoices.filter((i) => i.status === 'sent' || i.status === 'partial').length;
    if (size === 'compact') {
      return <KpiTile value={String(openCount)} label="open invoices" />;
    }
    return (
      <div style={{ padding: 'var(--space-1)' }}>
        <div style={bigNum}>{openCount}</div>
        <div style={subText}>open invoices · amounts hidden</div>
        <div style={{ ...calloutStyle(COLOR_NEUTRAL), marginTop: 'var(--space-3)' }}>
          AR amounts require <code>invoices.view_financials</code>.
        </div>
      </div>
    );
  }

  if (total === 0) return <WidgetEmptyState text="No outstanding receivables." />;

  const data = [
    { label: 'Current', key: 'current' as const, value: buckets.current, color: COLOR_OK },
    { label: '30+', key: 'd30' as const, value: buckets.d30, color: 'var(--color-warning)' },
    { label: '60+', key: 'd60' as const, value: buckets.d60, color: 'var(--color-caution)' },
    { label: '90+', key: 'd90' as const, value: buckets.d90, color: COLOR_DANGER },
  ];

  if (size === 'compact') {
    return (
      <KpiTile
        value={fmtMoney(total)}
        label={overdueCount > 0 ? `${overdueCount} overdue` : 'outstanding AR'}
        color={buckets.d90 > 0 ? COLOR_DANGER : COLOR_INFO}
        onClick={() => ctx.onDrill?.({ kind: 'invoice-list', status: 'all' })}
        ariaLabel={`${fmtMoney(total)} outstanding AR. Open invoices`}
      />
    );
  }

  const bucketTile = (d: (typeof data)[number], big: boolean) => (
    <button
      key={d.label}
      onClick={() =>
        ctx.onDrill?.({
          kind: 'invoice-list',
          status: d.key === 'current' ? 'all' : 'overdue',
        })
      }
      aria-label={`${d.label} bucket: ${fmtMoney(d.value)}. Open invoices`}
      style={{
        padding: big ? 'var(--space-3) var(--space-2)' : 'var(--space-2)',
        borderRadius: big ? 'var(--radius-lg)' : 'var(--radius-md)',
        background: `color-mix(in srgb, ${d.color} 10%, transparent)`,
        border: `1px solid color-mix(in srgb, ${d.color} 35%, transparent)`,
        textAlign: 'center',
        cursor: 'pointer',
        fontFamily: 'inherit',
      }}
    >
      <div
        style={{
          fontSize: big ? 'var(--font-size-xl)' : 'var(--font-size-md)',
          fontWeight: 700,
          color: d.color,
        }}
      >
        {fmtMoney(d.value)}
      </div>
      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--tm)', fontWeight: 600 }}>
        {d.label}
        {big ? ' days' : ''}
      </div>
      {big && (
        <div
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--tm)',
            marginTop: 'var(--space-1)',
          }}
        >
          {perBucketInvoices[d.key].length}{' '}
          {perBucketInvoices[d.key].length === 1 ? 'invoice' : 'invoices'}
        </div>
      )}
    </button>
  );

  if (size === 'standard') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)',
          padding: 'var(--space-1)',
        }}
      >
        <div>
          <div style={bigNum}>{fmtMoney(total)}</div>
          <div style={subText}>
            outstanding AR · {invoices.filter((i) => i.status !== 'paid').length} open
          </div>
        </div>
        <div
          style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--space-2)' }}
        >
          {data.map((d) => bucketTile(d, false))}
        </div>
      </div>
    );
  }

  // EXPANDED — buckets + invoice list
  const allOpenInvoices = [
    ...perBucketInvoices.d90,
    ...perBucketInvoices.d60,
    ...perBucketInvoices.d30,
    ...perBucketInvoices.current,
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div style={statRow}>
        <div>
          <div style={heroNum}>{fmtMoney(total)}</div>
          <div style={subText}>outstanding AR</div>
        </div>
        <div>
          <div style={{ ...heroNum, color: overdueCount > 0 ? COLOR_DANGER : COLOR_OK }}>
            {overdueCount}
          </div>
          <div style={subText}>overdue invoices</div>
        </div>
      </div>
      <div
        style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--space-2)' }}
      >
        {data.map((d) => bucketTile(d, true))}
      </div>
      <div>
        <div style={sectionLabel}>Open invoices · click to view</div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-1)',
            maxHeight: 200,
            overflow: 'auto',
          }}
        >
          {allOpenInvoices.length === 0 && (
            <div style={{ ...subText, padding: 'var(--space-2)' }}>No open invoices.</div>
          )}
          {allOpenInvoices.map((inv) => {
            const issued = (inv as any).issueDate || (inv as any).date;
            const days = issued
              ? Math.floor((Date.now() - new Date(issued).getTime()) / 86400000)
              : 0;
            const open = (inv.total || 0) - ((inv as any).amountPaid || 0);
            const ageColor =
              days > 90
                ? COLOR_DANGER
                : days > 60
                  ? COLOR_WARN
                  : days > 30
                    ? 'var(--color-warning)'
                    : COLOR_OK;
            return (
              <Row
                key={inv.id}
                onClick={() => ctx.onDrill?.({ kind: 'invoice', invoiceId: inv.id })}
                ariaLabel={`Open invoice ${inv.invoiceNumber}`}
              >
                <span
                  style={{
                    flex: 1,
                    fontSize: 'var(--font-size-base)',
                    color: 'var(--tx)',
                    fontFamily: 'var(--font-mono)',
                  }}
                >
                  {inv.invoiceNumber}
                </span>
                <span style={{ ...subText, width: 100 }}>{issued ? shortDate(issued) : '—'}</span>
                <span
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    color: ageColor,
                    width: 70,
                    textAlign: 'right',
                    fontWeight: 600,
                  }}
                >
                  {days}d old
                </span>
                <span
                  style={{
                    fontSize: 'var(--font-size-base)',
                    color: 'var(--tx)',
                    fontWeight: 700,
                    width: 100,
                    textAlign: 'right',
                  }}
                >
                  {fmtMoney(open)}
                </span>
              </Row>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// 5. BURN RATE — last 8 weeks (with forecast overlay in expanded)
//    compact  = avg/wk hero
//    standard = total + trend line
//    expanded = stats + actual/forecast overlay + budget pace ref
// ═══════════════════════════════════════════════════════════════

export function BurnRateWidget({ ctx, size }: { ctx: ProjectWidgetCtx; size: Size }) {
  const [entries, setEntries] = useState<HourEntry[]>([]);
  const [forecasts, setForecasts] = useState<HourForecast[]>([]);
  const [budgets, setBudgets] = useState<ProjectBudget[]>([]);
  const [loading, setLoading] = useState(true);

  const today = useMemo(() => todayIso(), []);
  const start = useMemo(() => addDays(today, -7 * 8), [today]);
  const futureEnd = useMemo(() => addDays(today, 7 * 4), [today]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const [eR, fcsR, bR] = await Promise.allSettled([
        TimeService.listEntries(ctx.project.id, start, today),
        TimeService.listForecastsForRange(today, futureEnd),
        ProjectBudgetService.listForProject(ctx.project.id),
      ]);
      if (!mounted) return;
      if (eR.status === 'fulfilled') setEntries(eR.value);
      if (fcsR.status === 'fulfilled')
        setForecasts(fcsR.value.filter((f) => f.projectId === ctx.project.id));
      if (bR.status === 'fulfilled') setBudgets(bR.value);
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, [ctx.project.id, start, today, futureEnd]);

  const totalBudgetHours = useMemo(() => {
    const total = budgets.find((b) => !b.phaseId && !b.billingRoleId);
    if (total?.budgetHours) return total.budgetHours;
    return ProjectBudgetService.computeRollup(budgets).hours;
  }, [budgets]);

  // 8 past + 4 future weeks
  const weekData = useMemo(() => {
    const buckets: Record<
      string,
      { actual: number; forecast: number; isFuture: boolean; weekStart: string }
    > = {};
    for (let w = 0; w < 8; w++) {
      const ws = mondayOf(addDays(today, -7 * (7 - w)));
      buckets[ws] = { actual: 0, forecast: 0, isFuture: false, weekStart: ws };
    }
    for (let w = 1; w <= 4; w++) {
      const ws = mondayOf(addDays(today, 7 * w));
      buckets[ws] = { actual: 0, forecast: 0, isFuture: true, weekStart: ws };
    }
    for (const e of entries) {
      const ws = mondayOf(e.date);
      if (ws in buckets) buckets[ws].actual += Number(e.hours) || 0;
    }
    for (const f of forecasts) {
      const ws = mondayOf(f.dayDate || f.weekStartDate);
      if (ws in buckets && buckets[ws].isFuture) buckets[ws].forecast += Number(f.hours) || 0;
    }
    return Object.entries(buckets)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([weekStart, d]) => ({
        label: shortWeek(weekStart),
        weekStart,
        actual: d.isFuture ? null : d.actual,
        forecast: d.isFuture ? d.forecast : null,
        isFuture: d.isFuture,
      }));
  }, [entries, forecasts, today]);

  const pastWeeks = weekData.filter((w) => !w.isFuture);
  const totalHours = pastWeeks.reduce((s, w) => s + (w.actual || 0), 0);
  const avgPerWeek = pastWeeks.length > 0 ? totalHours / pastWeeks.length : 0;

  // Budget pace: budget / total project weeks (we don't know that, so
  // use a rolling target = budget / 12 wks as a sanity reference)
  const budgetPace = totalBudgetHours > 0 ? totalBudgetHours / 12 : 0;

  if (loading) return <WidgetSkeleton lines={2} />;

  if (size === 'compact') {
    return (
      <KpiTile
        value={fmtHours(avgPerWeek)}
        label="avg/wk · last 8 wks"
        onClick={() => ctx.onDrill?.({ kind: 'time-entry-list' })}
        ariaLabel={`${fmtHours(avgPerWeek)} average per week. Open time entries`}
      />
    );
  }

  if (size === 'standard') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)',
          padding: 'var(--space-1)',
          height: '100%',
        }}
      >
        <div style={statRow}>
          <div>
            <div style={bigNum}>{fmtHours(totalHours)}</div>
            <div style={subText}>last 8 weeks · {fmtHours(avgPerWeek)} avg/wk</div>
          </div>
        </div>
        <div style={{ flex: 1, minHeight: 100 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={pastWeeks} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--bd)" />
              <XAxis dataKey="label" tick={axisTick} />
              <YAxis tick={axisTick} />
              <Tooltip
                contentStyle={chartTooltipStyle}
                formatter={(v: any) => [`${v}h`, 'Hours']}
              />
              <Line
                type="monotone"
                dataKey="actual"
                stroke={COLOR_INFO}
                strokeWidth={2}
                dot={{ r: 3 }}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  // EXPANDED: actual + forecast overlay + budget pace line
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-4)',
        height: '100%',
      }}
    >
      <div style={statRow}>
        <div>
          <div style={heroNum}>{fmtHours(totalHours)}</div>
          <div style={subText}>last 8 weeks</div>
        </div>
        <div>
          <div style={heroNum}>{fmtHours(avgPerWeek)}</div>
          <div style={subText}>avg / week</div>
        </div>
        <div>
          <div style={{ ...heroNum, color: COLOR_FORECAST }}>
            {fmtHours(
              weekData.filter((w) => w.isFuture).reduce((s, w) => s + (w.forecast || 0), 0)
            )}
          </div>
          <div style={subText}>forecast next 4 wks</div>
        </div>
      </div>
      <div style={{ flex: 1, minHeight: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={weekData} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--bd)" />
            <XAxis dataKey="label" tick={axisTick} />
            <YAxis tick={axisTick} />
            <Tooltip
              contentStyle={chartTooltipStyle}
              formatter={(v: any, n: any) => [
                v != null ? `${v}h` : '—',
                n === 'actual' ? 'Actual' : 'Forecast',
              ]}
            />
            <Line
              type="monotone"
              dataKey="actual"
              stroke={COLOR_INFO}
              strokeWidth={2}
              dot={{ r: 3 }}
              connectNulls
              name="Actual"
            />
            <Line
              type="monotone"
              dataKey="forecast"
              stroke={COLOR_FORECAST}
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ r: 3 }}
              connectNulls
              name="Forecast"
            />
            {budgetPace > 0 && (
              <ReferenceLine
                y={budgetPace}
                stroke={COLOR_NEUTRAL}
                strokeDasharray="2 4"
                label={{
                  value: `${fmtHours(budgetPace)} budget pace`,
                  fill: 'var(--tm)',
                  fontSize: 10,
                  position: 'right',
                }}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div style={{ ...subText, fontSize: 'var(--font-size-xs)', padding: '0 var(--space-1)' }}>
        Solid line = actual hours logged. Dashed = forecast. Dotted ref = budget ÷ 12wk pace.
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// 6. TEAM UTILIZATION — per-member, this week + 4-wk trajectory
//    compact  = hero this-week total
//    standard = per-member capacity bars
//    expanded = stats + capacity bars + 4-week trajectory microbars
// ═══════════════════════════════════════════════════════════════

export function TeamUtilizationWidget({ ctx, size }: { ctx: ProjectWidgetCtx; size: Size }) {
  const [forecasts, setForecasts] = useState<HourForecast[]>([]);
  const [loading, setLoading] = useState(true);

  const weekStart = useMemo(() => mondayOf(todayIso()), []);
  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);
  const fourWeekEnd = useMemo(() => addDays(weekStart, 27), [weekStart]);
  const memberIds = useMemo(() => ctx.assignments.map((a) => a.teamMemberId), [ctx.assignments]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (memberIds.length === 0) {
        setForecasts([]);
        setLoading(false);
        return;
      }
      const fcs = await TimeService.listForecastsForRange(weekStart, fourWeekEnd, memberIds);
      if (!mounted) return;
      setForecasts(fcs);
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, [memberIds.join(','), weekStart, fourWeekEnd]);

  const rows = useMemo(() => {
    return ctx.assignments
      .map((a) => {
        const m = ctx.members.find((x) => x.id === a.teamMemberId);
        if (!m) return null;
        // This week ON THIS PROJECT
        const thisWeek = forecasts
          .filter(
            (f) =>
              f.teamMemberId === m.id &&
              f.projectId === ctx.project.id &&
              (f.dayDate || f.weekStartDate) <= weekEnd
          )
          .reduce((s, f) => s + (Number(f.hours) || 0), 0);
        // 4-week total ON THIS PROJECT
        const fourWk = forecasts
          .filter((f) => f.teamMemberId === m.id && f.projectId === ctx.project.id)
          .reduce((s, f) => s + (Number(f.hours) || 0), 0);
        // Per-week trajectory ON THIS PROJECT
        const traj: number[] = [];
        for (let w = 0; w < 4; w++) {
          const ws = addDays(weekStart, w * 7);
          const we = addDays(ws, 6);
          const sum = forecasts
            .filter(
              (f) =>
                f.teamMemberId === m.id &&
                f.projectId === ctx.project.id &&
                (f.dayDate || f.weekStartDate) >= ws &&
                (f.dayDate || f.weekStartDate) <= we
            )
            .reduce((s, f) => s + (Number(f.hours) || 0), 0);
          traj.push(sum);
        }
        const capacity =
          m.weeklyCapacityHours ?? (m.employmentType === 'part_time_hourly' ? 20 : 40);
        const pct = capacity > 0 ? (thisWeek / capacity) * 100 : 0;
        return { id: m.id, name: m.name, thisWeek, fourWk, traj, capacity, pct };
      })
      .filter(Boolean) as {
      id: string;
      name: string;
      thisWeek: number;
      fourWk: number;
      traj: number[];
      capacity: number;
      pct: number;
    }[];
  }, [forecasts, ctx.assignments, ctx.members, ctx.project.id, weekStart, weekEnd]);

  const totalThisWeek = rows.reduce((s, r) => s + r.thisWeek, 0);
  const totalCapacity = rows.reduce((s, r) => s + r.capacity, 0);
  const overallPct = totalCapacity > 0 ? (totalThisWeek / totalCapacity) * 100 : 0;
  const totalFourWk = rows.reduce((s, r) => s + r.fourWk, 0);

  if (loading) return <WidgetSkeleton lines={2} />;
  if (rows.length === 0)
    return <WidgetEmptyState text="No team assigned. Add members in the Project Team widget." />;

  const capacityBar = (r: (typeof rows)[number], width: number) => (
    <div
      style={{
        width,
        height: 6,
        background: 'var(--sa)',
        borderRadius: 'var(--radius-sm)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          width: `${Math.min(100, r.pct)}%`,
          height: '100%',
          background: r.pct > 100 ? COLOR_DANGER : r.pct > 80 ? COLOR_WARN : COLOR_OK,
        }}
      />
    </div>
  );

  if (size === 'compact') {
    return <KpiTile value={fmtHours(totalThisWeek)} label={`this wk · ${rows.length} on team`} />;
  }

  if (size === 'standard') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)',
          padding: 'var(--space-1)',
        }}
      >
        <div style={statRow}>
          <div>
            <div style={bigNum}>{fmtHours(totalThisWeek)}</div>
            <div style={subText}>
              {rows.length} members · {fmtPct(overallPct)} of combined capacity
            </div>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-1)',
            maxHeight: 200,
            overflow: 'auto',
          }}
        >
          {rows.map((r) => (
            <Row
              key={r.id}
              onClick={() => ctx.onDrill?.({ kind: 'team-member', memberId: r.id })}
              ariaLabel={`View ${r.name}`}
            >
              <div
                style={{
                  flex: 1,
                  fontSize: 'var(--font-size-base)',
                  color: 'var(--tx)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {r.name}
              </div>
              {capacityBar(r, 100)}
              <div
                style={{
                  ...subText,
                  fontFamily: 'var(--font-mono)',
                  width: 56,
                  textAlign: 'right',
                }}
              >
                {fmtHours(r.thisWeek)}
              </div>
            </Row>
          ))}
        </div>
      </div>
    );
  }

  // EXPANDED — per-member 4-week trajectory
  const maxTraj = Math.max(40, ...rows.flatMap((r) => r.traj));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div style={statRow}>
        <div>
          <div style={heroNum}>{fmtHours(totalThisWeek)}</div>
          <div style={subText}>this week</div>
        </div>
        <div>
          <div style={heroNum}>{fmtHours(totalFourWk)}</div>
          <div style={subText}>next 4 weeks</div>
        </div>
        <div>
          <div style={heroNum}>{fmtPct(overallPct)}</div>
          <div style={subText}>combined capacity</div>
        </div>
      </div>
      <div>
        <div style={sectionLabel}>Per-person · this week + 4-week trajectory</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
          {rows.map((r) => (
            <Row
              key={r.id}
              onClick={() => ctx.onDrill?.({ kind: 'team-member', memberId: r.id })}
              ariaLabel={`View ${r.name}`}
              style={{ gap: 'var(--space-3)', padding: 'var(--space-2)' }}
            >
              <div
                style={{
                  flex: 1,
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 600,
                  color: 'var(--tx)',
                }}
              >
                {r.name}
              </div>
              {capacityBar(r, 90)}
              <div
                style={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'flex-end',
                  height: 24,
                  width: 100,
                }}
              >
                {r.traj.map((h, i) => {
                  const hPct = (h / maxTraj) * 100;
                  return (
                    <div
                      key={i}
                      title={`Wk ${i + 1}: ${fmtHours(h)}`}
                      style={{
                        flex: 1,
                        height: `${Math.max(2, hPct)}%`,
                        background: i === 0 ? COLOR_INFO : COLOR_FORECAST,
                        borderRadius: 1,
                        opacity: h > 0 ? 1 : 0.2,
                      }}
                    />
                  );
                })}
              </div>
              <div
                style={{
                  ...subText,
                  fontFamily: 'var(--font-mono)',
                  width: 80,
                  textAlign: 'right',
                }}
              >
                {fmtHours(r.thisWeek)} now
              </div>
              <div
                style={{
                  ...subText,
                  fontFamily: 'var(--font-mono)',
                  width: 80,
                  textAlign: 'right',
                }}
              >
                {fmtHours(r.fourWk)} 4wk
              </div>
            </Row>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// 7. PROJECT PHASE — the phase stepper, standalone.
//    Phases are timekeeping / forecasting context only (they stamp
//    hour entries + forecasts via projects.current_phase_id) — they
//    are NOT a parent for deliverables.
//    compact  = current phase + step dots
//    standard = full stepper, click-to-set (permission-gated)
// ═══════════════════════════════════════════════════════════════

export function PhasesWidget({ ctx, size }: { ctx: ProjectWidgetCtx; size: Size }) {
  const [busy, setBusy] = useState(false);
  const phases = useMemo(
    () => [...(ctx.phases || [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    [ctx.phases]
  );

  if (phases.length === 0) {
    return <WidgetEmptyState text="No phases configured. Add default phases in Admin Center." />;
  }

  const current =
    phases.find((p) => p.id === ctx.project.currentPhaseId) ||
    phases.find((p) => p.status === 'in-progress');
  const doneCount = phases.filter((p) => p.status === 'complete').length;
  const currentIdx = current ? phases.indexOf(current) : -1;
  const allDone = doneCount === phases.length;

  const phaseColor = (p: ProjectPhase) =>
    p.status === 'complete' ? COLOR_OK : p.status === 'in-progress' ? COLOR_INFO : COLOR_NEUTRAL;

  // ─── COMPACT — current phase + progress dots ─────
  if (size === 'compact') {
    return (
      <div
        onClick={current ? () => ctx.onDrill?.({ kind: 'phase', phaseId: current.id }) : undefined}
        role={current ? 'button' : undefined}
        tabIndex={current ? 0 : undefined}
        aria-label={
          current
            ? `Current phase ${current.name}, step ${currentIdx + 1} of ${phases.length}`
            : allDone
              ? 'All phases complete'
              : 'No phase in progress'
        }
        onKeyDown={(e) => {
          if (current && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            ctx.onDrill?.({ kind: 'phase', phaseId: current.id });
          }
        }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)',
          padding: 'var(--space-2) var(--space-1)',
          height: '100%',
          justifyContent: 'center',
          cursor: current ? 'pointer' : 'default',
          borderRadius: 'var(--radius-sm)',
        }}
      >
        <div
          style={{
            fontSize: 'var(--font-size-lg)',
            fontWeight: 700,
            color: allDone ? COLOR_OK : current ? COLOR_INFO : 'var(--tx)',
            lineHeight: 1.2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {allDone ? 'Complete' : current ? current.name : 'Not started'}
        </div>
        <div style={subText}>
          {allDone
            ? `all ${phases.length} phases done`
            : current
              ? `step ${currentIdx + 1} of ${phases.length}`
              : `${doneCount} of ${phases.length} complete`}
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-1)' }} aria-hidden="true">
          {phases.map((p) => (
            <div
              key={p.id}
              title={p.name}
              style={{
                flex: 1,
                height: 6,
                borderRadius: 'var(--radius-sm)',
                background:
                  p.status === 'pending'
                    ? 'var(--sa)'
                    : `color-mix(in srgb, ${phaseColor(p)} 80%, transparent)`,
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  // ─── STANDARD — full stepper, click-to-set ─────
  const canSet = !!ctx.canSetPhase && !!ctx.onSetPhase;
  const handleSet = async (p: ProjectPhase) => {
    if (!canSet || busy) return;
    setBusy(true);
    try {
      await ctx.onSetPhase!(p.name);
    } finally {
      setBusy(false);
    }
  };

  // Slim connected rail — dots joined by a line, one row per phase.
  // No boxed rows: the stepper reads as one continuous path.
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {phases.map((ph, idx) => {
        const isCurrent = ph.status === 'in-progress';
        const isDone = ph.status === 'complete';
        const isLast = idx === phases.length - 1;
        const railColor = isDone ? 'var(--ok)' : isCurrent ? 'var(--color-info)' : 'var(--bd)';
        return (
          <div
            key={ph.id}
            onClick={canSet ? () => handleSet(ph) : undefined}
            role={canSet ? 'button' : undefined}
            tabIndex={canSet ? 0 : undefined}
            aria-label={
              canSet
                ? `${ph.name} — ${isDone ? 'complete' : isCurrent ? 'in progress, click to complete' : 'pending, click to start'}`
                : `${ph.name} — ${isDone ? 'complete' : isCurrent ? 'in progress' : 'pending'}`
            }
            onKeyDown={
              canSet
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSet(ph);
                    }
                  }
                : undefined
            }
            onMouseEnter={(e) => {
              if (canSet) (e.currentTarget as HTMLDivElement).style.background = 'var(--sa)';
            }}
            onMouseLeave={(e) => {
              if (canSet) (e.currentTarget as HTMLDivElement).style.background = 'transparent';
            }}
            style={{
              display: 'flex',
              alignItems: 'stretch',
              gap: 'var(--space-3)',
              padding: '0 var(--space-2)',
              borderRadius: 'var(--radius-md)',
              cursor: canSet ? 'pointer' : 'default',
              opacity: busy ? 0.6 : 1,
              transition: 'background var(--duration-fast) var(--ease-out)',
            }}
          >
            {/* Rail cell: dot + connecting line */}
            <div
              aria-hidden="true"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: 20,
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  width: 1.5,
                  flex: '0 0 8px',
                  background: idx === 0 ? 'transparent' : 'var(--bd)',
                }}
              />
              <div
                style={{
                  width: isCurrent ? 18 : 14,
                  height: isCurrent ? 18 : 14,
                  borderRadius: 'var(--radius-full)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  background: isDone ? 'var(--ok)' : isCurrent ? 'var(--color-info)' : 'var(--sf)',
                  border: `1.5px solid ${railColor}`,
                  color: 'var(--color-text-inverse)',
                }}
              >
                {isDone && <Check size={9} strokeWidth={3} aria-hidden="true" />}
              </div>
              <div
                style={{
                  width: 1.5,
                  flex: 1,
                  background: isLast ? 'transparent' : 'var(--bd)',
                }}
              />
            </div>
            {/* Label row */}
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                minHeight: 36,
              }}
            >
              <span
                style={{
                  flex: 1,
                  fontSize: 'var(--font-size-base)',
                  fontWeight: isCurrent ? 600 : 400,
                  color: isCurrent || isDone ? 'var(--tx)' : 'var(--tm)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {ph.name}
              </span>
              <span
                style={{
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: isCurrent ? 600 : 400,
                  color: isDone ? 'var(--ok)' : isCurrent ? 'var(--color-info)' : 'var(--tm)',
                  flexShrink: 0,
                }}
              >
                {isDone ? 'Complete' : isCurrent ? 'In progress' : ''}
              </span>
            </div>
          </div>
        );
      })}
      {canSet && (
        <div
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--tm)',
            marginTop: 'var(--space-2)',
            paddingLeft: 'var(--space-2)',
          }}
        >
          Click once = In Progress. Click again = Complete.
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// 8. DELIVERABLES — standalone (name + due date + project, no phase).
//    compact  = overdue / next-due hero
//    standard = compact list + quick add
//    expanded = stats + Gantt-style timeline + full inline editor
// ═══════════════════════════════════════════════════════════════

export function DeliverablesWidget({ ctx, size }: { ctx: ProjectWidgetCtx; size: Size }) {
  const [newName, setNewName] = useState('');
  const [newDue, setNewDue] = useState('');
  const [saving, setSaving] = useState(false);
  const deliverables = ctx.deliverables || [];
  const today = todayIso();
  const canEdit = !!ctx.canEditDeliverables && !!ctx.onSaveDeliverable;

  const sorted = useMemo(
    () =>
      [...deliverables].sort((a, b) => (a.dueDate || '9999').localeCompare(b.dueDate || '9999')),
    [deliverables]
  );
  const overdue = sorted.filter(
    (d) => d.status !== 'complete' && d.dueDate && daysBetween(today, d.dueDate) < 0
  );
  const upcoming = sorted.filter(
    (d) => d.status !== 'complete' && (!d.dueDate || daysBetween(today, d.dueDate) >= 0)
  );
  const complete = sorted.filter((d) => d.status === 'complete');
  const issuedCount = deliverables.filter((d) => d.issued).length;
  const nextDue = upcoming.find((d) => d.dueDate);

  const statusColor = (d: Deliverable) => {
    if (d.status === 'complete') return COLOR_OK;
    const days = d.dueDate ? daysBetween(today, d.dueDate) : null;
    if (days === null) return COLOR_NEUTRAL;
    if (days < 0) return COLOR_DANGER;
    if (days < 7) return COLOR_WARN;
    return COLOR_INFO;
  };

  const dueLabel = (d: Deliverable): { text: string; color: string; bold: boolean } => {
    if (d.status === 'complete') return { text: 'Done', color: COLOR_OK, bold: false };
    if (!d.dueDate) return { text: 'no date', color: 'var(--tm)', bold: false };
    const days = daysBetween(today, d.dueDate);
    if (days < 0) return { text: `${Math.abs(days)}d late`, color: COLOR_DANGER, bold: true };
    if (days < 7) return { text: `${days}d`, color: COLOR_WARN, bold: false };
    return { text: `${days}d`, color: 'var(--tm)', bold: false };
  };

  const patch = async (d: Deliverable, p: Partial<Deliverable>) => {
    if (!ctx.onSaveDeliverable) return;
    await ctx.onSaveDeliverable({ ...d, ...p });
  };

  const addNew = async () => {
    if (!newName.trim() || !ctx.onSaveDeliverable || saving) return;
    setSaving(true);
    try {
      await ctx.onSaveDeliverable({
        id: '',
        projectId: ctx.project.id,
        name: newName.trim(),
        description: '',
        dueDate: newDue || undefined,
        status: 'pending',
        issued: false,
        createdAt: '',
        updatedAt: '',
      } as Deliverable);
      setNewName('');
      setNewDue('');
    } finally {
      setSaving(false);
    }
  };

  // Ghost add row — reads as the next empty line of the list, not a form
  // (the zero-friction path: a deliverable is just a name + optional date).
  const quickAdd = canEdit ? (
    <div
      style={{
        display: 'flex',
        gap: 'var(--space-2)',
        alignItems: 'center',
        minHeight: 32,
        padding: '0 var(--space-2)',
        borderRadius: 'var(--radius-sm)',
      }}
    >
      <Plus size={13} aria-hidden="true" style={{ color: 'var(--tm)', flexShrink: 0 }} />
      <input
        type="text"
        placeholder="Add deliverable…"
        aria-label="New deliverable name"
        value={newName}
        onChange={(e) => setNewName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') addNew();
        }}
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 'var(--font-size-base)',
          fontFamily: 'var(--font-sans)',
          border: 'none',
          background: 'transparent',
          color: 'var(--tx)',
          padding: 'var(--space-1) 0',
        }}
      />
      <input
        type="date"
        aria-label="New deliverable due date"
        value={newDue}
        onChange={(e) => setNewDue(e.target.value)}
        style={{
          fontSize: 'var(--font-size-sm)',
          fontFamily: 'var(--font-sans)',
          border: 'none',
          background: 'transparent',
          color: newDue ? 'var(--tx)' : 'var(--tm)',
          padding: 'var(--space-1) 0',
        }}
      />
      {newName.trim() && (
        <button
          onClick={addNew}
          disabled={saving}
          aria-label="Add deliverable"
          style={btnPrimary(saving)}
        >
          Add
        </button>
      )}
    </div>
  ) : null;

  // ─── COMPACT ─────
  if (size === 'compact') {
    if (deliverables.length === 0) {
      return <KpiTile value="0" label="deliverables yet" />;
    }
    if (overdue.length > 0) {
      const worst = overdue[0];
      return (
        <KpiTile
          value={String(overdue.length)}
          label={`overdue${nextDue ? ` · next: ${nextDue.name}` : ` · ${worst.name}`}`}
          color={COLOR_DANGER}
          onClick={() => ctx.onDrill?.({ kind: 'deliverable', deliverableId: worst.id })}
          ariaLabel={`${overdue.length} overdue deliverables. View deliverables`}
        />
      );
    }
    if (nextDue && nextDue.dueDate) {
      const days = daysBetween(today, nextDue.dueDate);
      return (
        <KpiTile
          value={`${days}d`}
          label={`until ${nextDue.name}`}
          color={days < 7 ? COLOR_WARN : COLOR_INFO}
          onClick={() => ctx.onDrill?.({ kind: 'deliverable', deliverableId: nextDue.id })}
          ariaLabel={`${days} days until ${nextDue.name}. View deliverables`}
        />
      );
    }
    return <KpiTile value={String(upcoming.length)} label="upcoming deliverables" />;
  }

  // ─── STANDARD — compact list + quick add ─────
  if (size === 'standard') {
    const listed = [...overdue, ...upcoming.slice(0, 8)];
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)',
          padding: 'var(--space-1)',
        }}
      >
        {(overdue.length > 0 || upcoming.length > 0 || complete.length > 0) && (
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            {overdue.length > 0 && (
              <span style={countChip(COLOR_DANGER)}>{overdue.length} overdue</span>
            )}
            {upcoming.length > 0 && (
              <span style={countChip(COLOR_INFO)}>{upcoming.length} upcoming</span>
            )}
            {complete.length > 0 && (
              <span style={countChip(COLOR_OK)}>{complete.length} complete</span>
            )}
          </div>
        )}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-1)',
            maxHeight: 170,
            overflow: 'auto',
          }}
        >
          {listed.length === 0 && (
            <div style={{ ...subText, fontStyle: 'italic', padding: 'var(--space-1) 0' }}>
              {deliverables.length === 0
                ? 'No deliverables yet — add the first one below.'
                : 'Nothing open. All deliverables complete.'}
            </div>
          )}
          {listed.map((d) => {
            const dl = dueLabel(d);
            return (
              <Row
                key={d.id}
                onClick={() => ctx.onDrill?.({ kind: 'deliverable', deliverableId: d.id })}
                ariaLabel={`${d.name}, ${dl.text}`}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 'var(--radius-full)',
                    background: statusColor(d),
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    flex: 1,
                    fontSize: 'var(--font-size-base)',
                    color: 'var(--tx)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {d.name}
                </span>
                {d.issued && <span style={issuedBadge}>ISSUED</span>}
                <span style={{ ...subText, width: 64, textAlign: 'right', flexShrink: 0 }}>
                  {d.dueDate ? shortDate(d.dueDate) : '—'}
                </span>
                <span
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    color: dl.color,
                    fontWeight: dl.bold ? 700 : 400,
                    width: 56,
                    textAlign: 'right',
                    flexShrink: 0,
                  }}
                >
                  {dl.text}
                </span>
              </Row>
            );
          })}
        </div>
        {quickAdd}
      </div>
    );
  }

  // ─── EXPANDED — stats + Gantt timeline + full editor ─────
  const dated = sorted.filter((d) => d.dueDate);
  const timeline = (() => {
    if (dated.length === 0) return null;
    const times = dated.flatMap((d) => {
      const t = [new Date(d.dueDate!).getTime()];
      if (d.sealDate) t.push(new Date(d.sealDate).getTime());
      return t;
    });
    const todayMs = new Date(today).getTime();
    let lo = Math.min(...times, todayMs);
    let hi = Math.max(...times, todayMs);
    const pad = Math.max((hi - lo) * 0.06, 7 * 864e5);
    lo -= pad;
    hi += pad;
    const span = hi - lo || 1;
    const pct = (ms: number) => ((ms - lo) / span) * 100;
    const ticks: { x: number; label: string }[] = [];
    const t = new Date(lo);
    t.setDate(1);
    t.setHours(0, 0, 0, 0);
    let guard = 0;
    while (t.getTime() <= hi && guard++ < 60) {
      const x = pct(t.getTime());
      if (x >= -1 && x <= 101)
        ticks.push({ x, label: t.toLocaleDateString(undefined, { month: 'short' }) });
      t.setMonth(t.getMonth() + 1);
    }
    const laneH = 26;
    const tx = pct(todayMs);
    return (
      <div>
        <div style={sectionLabel}>Timeline</div>
        <div
          style={{
            border: '1px solid var(--bd)',
            borderRadius: 'var(--radius-lg)',
            background: 'var(--bg)',
            padding: 'var(--space-4) var(--space-2) var(--space-2) 0',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'relative',
              marginLeft: 140,
              height: dated.length * laneH + 6,
            }}
          >
            {ticks.map((tk, i) => (
              <div
                key={i}
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: tk.x + '%',
                  top: -12,
                  bottom: 0,
                  borderLeft: '1px dashed var(--bd)',
                  pointerEvents: 'none',
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    top: -2,
                    left: 3,
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--tm)',
                  }}
                >
                  {tk.label}
                </span>
              </div>
            ))}
            {tx >= 0 && tx <= 100 && (
              <div
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: tx + '%',
                  top: -12,
                  bottom: 0,
                  borderLeft: `2px solid ${COLOR_DANGER}`,
                  pointerEvents: 'none',
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    top: -11,
                    left: -14,
                    fontSize: 'var(--font-size-xs)',
                    color: COLOR_DANGER,
                    fontWeight: 700,
                  }}
                >
                  TODAY
                </span>
              </div>
            )}
            {dated.map((d, i) => {
              const c = statusColor(d);
              const top = i * laneH;
              const dueMs = new Date(d.dueDate!).getTime();
              const sealMs = d.sealDate ? new Date(d.sealDate).getTime() : null;
              const barStart = sealMs !== null ? Math.min(sealMs, dueMs) : null;
              const barEnd = sealMs !== null ? Math.max(sealMs, dueMs) : null;
              return (
                <React.Fragment key={d.id}>
                  <div
                    onClick={() => ctx.onDrill?.({ kind: 'deliverable', deliverableId: d.id })}
                    role="button"
                    tabIndex={0}
                    aria-label={`${d.name}, due ${shortDate(d.dueDate!)}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        ctx.onDrill?.({ kind: 'deliverable', deliverableId: d.id });
                      }
                    }}
                    style={{
                      position: 'absolute',
                      left: -140,
                      width: 132,
                      top,
                      height: laneH,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-1)',
                      cursor: 'pointer',
                      overflow: 'hidden',
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: 'var(--radius-full)',
                        background: c,
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: d.status === 'complete' ? 400 : 500,
                        color: d.status === 'complete' ? 'var(--tm)' : 'var(--tx)',
                        textDecoration: d.status === 'complete' ? 'line-through' : 'none',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {d.name}
                    </span>
                  </div>
                  <div
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      top: top + laneH / 2 - 0.5,
                      height: 1,
                      background: 'var(--bd)',
                      opacity: 0.4,
                    }}
                  />
                  {barStart !== null && barEnd !== null && barEnd > barStart && (
                    <div
                      title={`${d.name} · seal ${shortDate(d.sealDate!)} → due ${shortDate(d.dueDate!)}`}
                      style={{
                        position: 'absolute',
                        left: pct(barStart) + '%',
                        width: Math.max(pct(barEnd) - pct(barStart), 0.6) + '%',
                        top: top + laneH / 2 - 4,
                        height: 8,
                        minWidth: 4,
                        borderRadius: 'var(--radius-sm)',
                        background: `color-mix(in srgb, ${c} 30%, transparent)`,
                        border: `1px solid color-mix(in srgb, ${c} 55%, transparent)`,
                      }}
                    />
                  )}
                  <div
                    title={`${d.name} · ${shortDate(d.dueDate!)}`}
                    onClick={() => ctx.onDrill?.({ kind: 'deliverable', deliverableId: d.id })}
                    style={{
                      position: 'absolute',
                      left: `calc(${pct(dueMs)}% - 5px)`,
                      top: top + laneH / 2 - 5,
                      width: 10,
                      height: 10,
                      transform: 'rotate(45deg)',
                      background: c,
                      border: '1.5px solid var(--sf)',
                      cursor: 'pointer',
                      borderRadius: 1,
                    }}
                  />
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    );
  })();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      {/* Stats */}
      <div style={statRow}>
        <div>
          <div style={{ ...bigNum, color: overdue.length > 0 ? COLOR_DANGER : 'var(--tx)' }}>
            {overdue.length}
          </div>
          <div style={subText}>overdue</div>
        </div>
        <div>
          <div style={bigNum}>{upcoming.length}</div>
          <div style={subText}>upcoming</div>
        </div>
        <div>
          <div style={{ ...bigNum, color: COLOR_OK }}>{complete.length}</div>
          <div style={subText}>complete</div>
        </div>
        <div>
          <div style={{ ...bigNum, color: COLOR_INFO }}>{issuedCount}</div>
          <div style={subText}>issued</div>
        </div>
      </div>

      {/* Gantt-style timeline */}
      {timeline ||
        (deliverables.length > 0 ? (
          <div style={{ ...subText, fontStyle: 'italic' }}>
            Add due dates to plot deliverables on the timeline.
          </div>
        ) : null)}

      {/* Full inline editor */}
      <div>
        <div style={sectionLabel}>All deliverables</div>
        {sorted.length === 0 && (
          <div style={{ ...subText, fontStyle: 'italic', padding: 'var(--space-1) 0' }}>
            No deliverables yet. Add the first one below.
          </div>
        )}
        {sorted.length > 0 && (
          <div>
            <div style={delGridRow(true)}>
              <span style={delHeadCell}>Deliverable</span>
              <span style={delHeadCell}>Due</span>
              <span style={delHeadCell}>Seal</span>
              <span style={{ ...delHeadCell, textAlign: 'center' }}>Issued</span>
              <span style={{ ...delHeadCell, textAlign: 'right' }}>Status</span>
              <span />
            </div>
            <div style={{ maxHeight: 280, overflow: 'auto' }}>
              {sorted.map((d) => {
                const dl = dueLabel(d);
                const isComplete = d.status === 'complete';
                return (
                  <div key={d.id} style={delGridRow(false)}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        minWidth: 0,
                      }}
                    >
                      <button
                        onClick={
                          canEdit
                            ? () => patch(d, { status: isComplete ? 'pending' : 'complete' })
                            : undefined
                        }
                        disabled={!canEdit}
                        title={isComplete ? 'Mark pending' : 'Mark complete'}
                        aria-label={`${d.name}: ${isComplete ? 'mark pending' : 'mark complete'}`}
                        style={{
                          width: 16,
                          height: 16,
                          borderRadius: 'var(--radius-full)',
                          cursor: canEdit ? 'pointer' : 'default',
                          flexShrink: 0,
                          background: isComplete ? COLOR_OK : 'transparent',
                          border: `2px solid ${isComplete ? COLOR_OK : statusColor(d)}`,
                          padding: 0,
                        }}
                      />
                      {canEdit ? (
                        <input
                          defaultValue={d.name}
                          aria-label="Deliverable name"
                          onBlur={(e) => {
                            if (e.target.value.trim() && e.target.value !== d.name)
                              patch(d, { name: e.target.value.trim() });
                          }}
                          style={{
                            flex: 1,
                            minWidth: 0,
                            fontSize: 'var(--font-size-base)',
                            fontWeight: 600,
                            border: 'none',
                            background: 'transparent',
                            color: 'var(--tx)',
                            textDecoration: isComplete ? 'line-through' : 'none',
                            fontFamily: 'var(--font-sans)',
                          }}
                        />
                      ) : (
                        <span
                          style={{
                            flex: 1,
                            minWidth: 0,
                            fontSize: 'var(--font-size-base)',
                            fontWeight: 600,
                            color: 'var(--tx)',
                            textDecoration: isComplete ? 'line-through' : 'none',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {d.name}
                        </span>
                      )}
                    </div>
                    <input
                      type="date"
                      defaultValue={d.dueDate || ''}
                      disabled={!canEdit}
                      aria-label={`${d.name} due date`}
                      onChange={(e) => patch(d, { dueDate: e.target.value || undefined })}
                      style={delDateInput(dl.bold)}
                    />
                    <input
                      type="date"
                      defaultValue={d.sealDate || ''}
                      disabled={!canEdit}
                      aria-label={`${d.name} seal date`}
                      onChange={(e) => patch(d, { sealDate: e.target.value || undefined })}
                      style={delDateInput(false)}
                    />
                    <div style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={d.issued}
                        disabled={!canEdit}
                        aria-label={`${d.name} issued`}
                        onChange={(e) => patch(d, { issued: e.target.checked })}
                        style={{ cursor: canEdit ? 'pointer' : 'default' }}
                      />
                    </div>
                    <span
                      style={{
                        fontSize: 'var(--font-size-sm)',
                        color: dl.color,
                        fontWeight: dl.bold ? 700 : 500,
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {dl.text}
                    </span>
                    {canEdit && ctx.onDeleteDeliverable ? (
                      <button
                        onClick={() => {
                          if (confirm(`Delete deliverable "${d.name}"?`))
                            ctx.onDeleteDeliverable!(d.id);
                        }}
                        title="Delete deliverable"
                        aria-label={`Delete ${d.name}`}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          color: 'var(--tm)',
                          cursor: 'pointer',
                          width: 28,
                          height: 28,
                          padding: 0,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 'var(--radius-sm)',
                        }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLButtonElement).style.color = 'var(--dg)';
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLButtonElement).style.color = 'var(--tm)';
                        }}
                      >
                        <X size={13} aria-hidden="true" />
                      </button>
                    ) : (
                      <span />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {quickAdd}
      </div>
    </div>
  );
}

// Deliverable editor grid — flat rows on the card surface (no box-in-box);
// a hairline under the caption row and between rows is the only chrome.
function delGridRow(isHead: boolean): React.CSSProperties {
  return {
    display: 'grid',
    gridTemplateColumns: 'minmax(0,1fr) 116px 116px 52px 60px 28px',
    gap: 'var(--space-2)',
    alignItems: 'center',
    padding: isHead ? '0 var(--space-2) var(--space-1)' : 'var(--space-1) var(--space-2)',
    minHeight: isHead ? undefined : 36,
    borderBottom: isHead
      ? '1px solid var(--bd)'
      : '1px solid color-mix(in srgb, var(--bd) 45%, transparent)',
  };
}

const delHeadCell: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 700,
  color: 'var(--tm)',
  textTransform: 'uppercase',
  letterSpacing: '.04em',
};

// Inline-edit register: date inputs are borderless text until focused
// (base.css :focus-visible supplies the ring).
function delDateInput(isOverdue: boolean): React.CSSProperties {
  return {
    fontSize: 'var(--font-size-sm)',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    background: 'transparent',
    color: isOverdue ? 'var(--dg)' : 'var(--tm)',
    padding: '2px 0',
    fontFamily: 'var(--font-sans)',
    minWidth: 0,
  };
}

function countChip(color: string): React.CSSProperties {
  return {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    padding: '2px var(--space-2)',
    borderRadius: 'var(--radius-full)',
    background: `color-mix(in srgb, ${color} 12%, transparent)`,
    color,
  };
}

const issuedBadge: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  padding: '1px var(--space-1)',
  borderRadius: 'var(--radius-sm)',
  background: 'rgba(var(--okr),.12)',
  color: 'var(--ok)',
  fontWeight: 700,
  flexShrink: 0,
};

// ═══════════════════════════════════════════════════════════════
// 9. PROFITABILITY — revenue − cost, margin %
//    compact  = profit hero
//    standard = profit + revenue/cost tiles
//    expanded = 4 stats + revenue-vs-cost bar + methodology note
// ═══════════════════════════════════════════════════════════════

export function ProfitabilityWidget({ ctx, size }: { ctx: ProjectWidgetCtx; size: Size }) {
  const canSeeMoney = hasPermission(ctx.userPermissions, 'projects.view_financials');
  const [entries, setEntries] = useState<HourEntry[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const [eR, invR] = await Promise.allSettled([
        TimeService.listEntries(ctx.project.id),
        InvoiceService.list(ctx.project.id).catch(() => [] as Invoice[]),
      ]);
      if (!mounted) return;
      if (eR.status === 'fulfilled') setEntries(eR.value);
      if (invR.status === 'fulfilled') setInvoices(invR.value);
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, [ctx.project.id]);

  // Revenue model: paid + sent invoice totals (committed revenue).
  // Cost model: hours × member rate (same calc as Cost widget).
  // Profit = revenue − cost. Margin = profit / revenue.
  const revenue = useMemo(() => {
    return invoices
      .filter((i) => i.status === 'sent' || i.status === 'paid' || i.status === 'partial')
      .reduce((s, i) => s + (i.total || 0), 0);
  }, [invoices]);

  const cost = useMemo(() => {
    let sum = 0;
    for (const e of entries) {
      const m = ctx.members.find((x) => x.id === e.teamMemberId);
      sum += (Number(e.hours) || 0) * projectMemberRate(m, ctx.billingRoles);
    }
    return sum;
  }, [entries, ctx.members, ctx.billingRoles]);

  const profit = revenue - cost;
  const margin = revenue > 0 ? (profit / revenue) * 100 : 0;
  const contractValue = ctx.project.contractValue || 0;
  const contractMargin = contractValue > 0 ? ((contractValue - cost) / contractValue) * 100 : null;

  const profitColor = profit < 0 ? COLOR_DANGER : margin < 15 ? COLOR_WARN : COLOR_OK;

  if (loading) return <WidgetSkeleton lines={2} />;

  if (!canSeeMoney) {
    return <WidgetEmptyState text="Profit detail requires projects.view_financials permission." />;
  }

  if (size === 'compact') {
    return (
      <KpiTile
        value={fmtMoney(profit)}
        label={`margin ${fmtPct(margin)}`}
        color={profitColor}
        onClick={() => ctx.onDrill?.({ kind: 'invoice-list' })}
        ariaLabel={`${fmtMoney(profit)} profit, ${fmtPct(margin)} margin. Open invoices`}
      />
    );
  }

  if (size === 'standard') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)',
          padding: 'var(--space-1)',
        }}
      >
        <div style={statRow}>
          <div>
            <div style={{ ...bigNum, color: profitColor }}>{fmtMoney(profit)}</div>
            <div style={subText}>profit · {fmtPct(margin)} margin</div>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)' }}>
          <div style={miniBox(COLOR_OK)}>
            <div style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, color: COLOR_OK }}>
              {fmtMoney(revenue)}
            </div>
            <div style={miniLabel}>revenue (invoiced)</div>
          </div>
          <div style={miniBox(COLOR_DANGER)}>
            <div style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, color: COLOR_DANGER }}>
              {fmtMoney(cost)}
            </div>
            <div style={miniLabel}>cost (hours × rate)</div>
          </div>
        </div>
      </div>
    );
  }

  // EXPANDED
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div style={statRow}>
        <div>
          <div style={{ ...heroNum, color: COLOR_OK }}>{fmtMoney(revenue)}</div>
          <div style={subText}>revenue invoiced</div>
        </div>
        <div>
          <div style={{ ...heroNum, color: COLOR_DANGER }}>{fmtMoney(cost)}</div>
          <div style={subText}>actual cost</div>
        </div>
        <div>
          <div style={{ ...heroNum, color: profitColor }}>{fmtMoney(profit)}</div>
          <div style={subText}>net (margin {fmtPct(margin)})</div>
        </div>
        <div>
          <div style={heroNum}>{fmtMoney(contractValue)}</div>
          <div style={subText}>
            contract value
            {contractMargin !== null && ` · proj. ${fmtPct(contractMargin)}`}
          </div>
        </div>
      </div>

      {revenue > 0 && (
        <div>
          <div style={sectionLabel}>Revenue vs cost</div>
          <div
            style={{
              position: 'relative',
              height: 28,
              borderRadius: 'var(--radius-md)',
              background: 'var(--sa)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: '100%',
                background: COLOR_OK,
                opacity: 0.3,
              }}
            />
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: `${Math.min(100, (cost / revenue) * 100)}%`,
                background: COLOR_DANGER,
                opacity: 0.7,
              }}
            />
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 700,
                color: 'var(--tx)',
              }}
            >
              {fmtMoney(cost)} cost on {fmtMoney(revenue)} revenue → {fmtPct(margin)} margin
            </div>
          </div>
        </div>
      )}

      <div style={calloutStyle(COLOR_NEUTRAL)}>
        Revenue = sum of sent + partial + paid invoices. Cost = hours logged × member rate (override
        or billing role default). Margin = (revenue − cost) / revenue.
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// 10. VARIANCE — forecast vs actual rolling 4 weeks
//    compact  = variance hero
//    standard = stats + grouped chart
//    expanded = 4 stats + chart + per-week rows + explainer
// ═══════════════════════════════════════════════════════════════

export function VarianceWidget({ ctx, size }: { ctx: ProjectWidgetCtx; size: Size }) {
  const [entries, setEntries] = useState<HourEntry[]>([]);
  const [forecasts, setForecasts] = useState<HourForecast[]>([]);
  const [loading, setLoading] = useState(true);

  const today = useMemo(() => todayIso(), []);
  const start = useMemo(() => addDays(mondayOf(today), -7 * 4), [today]);
  const end = useMemo(() => addDays(mondayOf(today), -1), [today]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const [eR, fcsR] = await Promise.allSettled([
        TimeService.listEntries(ctx.project.id, start, end),
        TimeService.listForecastsForRange(start, end),
      ]);
      if (!mounted) return;
      if (eR.status === 'fulfilled') setEntries(eR.value);
      if (fcsR.status === 'fulfilled')
        setForecasts(fcsR.value.filter((f) => f.projectId === ctx.project.id));
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, [ctx.project.id, start, end]);

  const weekData = useMemo(() => {
    const buckets: Record<string, { actual: number; forecast: number }> = {};
    for (let w = 0; w < 4; w++) {
      const ws = mondayOf(addDays(today, -7 * (4 - w)));
      buckets[ws] = { actual: 0, forecast: 0 };
    }
    for (const e of entries) {
      const ws = mondayOf(e.date);
      if (ws in buckets) buckets[ws].actual += Number(e.hours) || 0;
    }
    for (const f of forecasts) {
      const ws = mondayOf(f.dayDate || f.weekStartDate);
      if (ws in buckets) buckets[ws].forecast += Number(f.hours) || 0;
    }
    return Object.entries(buckets)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([ws, d]) => ({
        label: shortWeek(ws),
        weekStart: ws,
        forecast: d.forecast,
        actual: d.actual,
        variance: d.actual - d.forecast,
      }));
  }, [entries, forecasts, today]);

  const totalActual = weekData.reduce((s, w) => s + w.actual, 0);
  const totalForecast = weekData.reduce((s, w) => s + w.forecast, 0);
  const totalVariance = totalActual - totalForecast;
  const accuracyPct =
    totalForecast > 0 ? Math.max(0, 100 - (Math.abs(totalVariance) / totalForecast) * 100) : 0;
  const varColor =
    totalVariance > 0 && totalVariance / Math.max(1, totalForecast) > 0.1
      ? COLOR_WARN
      : totalVariance < 0 && Math.abs(totalVariance) / Math.max(1, totalForecast) > 0.1
        ? COLOR_DANGER
        : COLOR_OK;

  if (loading) return <WidgetSkeleton lines={2} />;
  if (totalForecast === 0 && totalActual === 0) {
    return <WidgetEmptyState text="No forecast or actuals in the last 4 weeks." />;
  }

  const varianceChart = (height: number) => (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={weekData} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--bd)" />
        <XAxis dataKey="label" tick={axisTick} />
        <YAxis tick={axisTick} />
        <Tooltip
          contentStyle={chartTooltipStyle}
          formatter={(v: any, n: any) => [`${v}h`, n === 'actual' ? 'Actual' : 'Forecast']}
        />
        <Bar dataKey="forecast" fill={COLOR_FORECAST} radius={[3, 3, 0, 0]} />
        <Bar dataKey="actual" fill={COLOR_INFO} radius={[3, 3, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );

  if (size === 'compact') {
    return (
      <KpiTile
        value={`${totalVariance > 0 ? '+' : ''}${fmtHours(totalVariance)}`}
        label={`vs forecast · ${fmtPct(accuracyPct)} accurate`}
        color={varColor}
      />
    );
  }

  if (size === 'standard') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)',
          padding: 'var(--space-1)',
        }}
      >
        <div style={statRow}>
          <div>
            <div style={{ ...bigNum, color: varColor }}>
              {totalVariance > 0 ? '+' : ''}
              {fmtHours(totalVariance)}
            </div>
            <div style={subText}>variance · {fmtPct(accuracyPct)} accurate</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={subText}>{fmtHours(totalActual)} actual</div>
            <div style={subText}>{fmtHours(totalForecast)} forecast</div>
          </div>
        </div>
        <div style={{ flex: 1, minHeight: 90 }}>{varianceChart(100)}</div>
      </div>
    );
  }

  // EXPANDED
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div style={statRow}>
        <div>
          <div style={bigNum}>{fmtHours(totalActual)}</div>
          <div style={subText}>actual</div>
        </div>
        <div>
          <div style={{ ...bigNum, color: COLOR_FORECAST }}>{fmtHours(totalForecast)}</div>
          <div style={subText}>forecast</div>
        </div>
        <div>
          <div style={{ ...bigNum, color: varColor }}>
            {totalVariance > 0 ? '+' : ''}
            {fmtHours(totalVariance)}
          </div>
          <div style={subText}>variance</div>
        </div>
        <div>
          <div style={bigNum}>{fmtPct(accuracyPct)}</div>
          <div style={subText}>accuracy</div>
        </div>
      </div>
      <div style={{ height: 200 }}>{varianceChart(200)}</div>
      <div>
        <div style={sectionLabel}>Per-week variance</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
          {weekData.map((w) => {
            const v = w.variance;
            const c = v > 0 ? COLOR_WARN : v < 0 ? COLOR_DANGER : COLOR_OK;
            return (
              <Row
                key={w.weekStart}
                onClick={() => ctx.onDrill?.({ kind: 'time-entry-list', weekStart: w.weekStart })}
                ariaLabel={`Week of ${w.label}: ${fmtHours(v)} variance. Open time entries`}
              >
                <span style={{ flex: 1, fontSize: 'var(--font-size-base)', color: 'var(--tx)' }}>
                  {w.label}
                </span>
                <span style={{ ...subText, width: 70, textAlign: 'right' }}>
                  fc {fmtHours(w.forecast)}
                </span>
                <span style={{ ...subText, width: 70, textAlign: 'right' }}>
                  ac {fmtHours(w.actual)}
                </span>
                <span
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    color: c,
                    fontWeight: 700,
                    width: 80,
                    textAlign: 'right',
                  }}
                >
                  {v > 0 ? '+' : ''}
                  {fmtHours(v)}
                </span>
              </Row>
            );
          })}
        </div>
      </div>
      <div style={calloutStyle(COLOR_NEUTRAL)}>
        Positive variance = burned MORE than forecast (over-pace). Negative = burned LESS
        (slipping). Aim for ±10%.
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// 11. RECENT ACTIVITY — entries / invoices / deliverables feed
//    standard = latest 8 events
//    expanded = hero count + full 30-day feed
//    (no compact — an activity feed has no honest one-number form)
// ═══════════════════════════════════════════════════════════════

interface ActivityItem {
  id: string;
  kind: 'entry' | 'invoice-sent' | 'invoice-paid' | 'deliverable-complete' | 'deliverable-issued';
  ts: string;
  label: string;
  detail?: string;
  drill?: DashboardDrill;
}

export function RecentActivityWidget({ ctx, size }: { ctx: ProjectWidgetCtx; size: Size }) {
  const [entries, setEntries] = useState<HourEntry[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const deliverables = ctx.deliverables || [];

  const today = useMemo(() => todayIso(), []);
  const start = useMemo(() => addDays(today, -30), [today]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const [eR, invR] = await Promise.allSettled([
        TimeService.listEntries(ctx.project.id, start, today),
        InvoiceService.list(ctx.project.id).catch(() => [] as Invoice[]),
      ]);
      if (!mounted) return;
      if (eR.status === 'fulfilled') setEntries(eR.value);
      if (invR.status === 'fulfilled') setInvoices(invR.value);
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, [ctx.project.id, start, today]);

  const items = useMemo(() => {
    const out: ActivityItem[] = [];
    // Time entries — group by date+member to keep noise down
    const byDayMember = new Map<string, { date: string; memberId: string; hours: number }>();
    for (const e of entries) {
      const k = `${e.date}|${e.teamMemberId}`;
      const cur = byDayMember.get(k) || { date: e.date, memberId: e.teamMemberId, hours: 0 };
      cur.hours += Number(e.hours) || 0;
      byDayMember.set(k, cur);
    }
    for (const v of byDayMember.values()) {
      const m = ctx.members.find((x) => x.id === v.memberId);
      out.push({
        id: `entry|${v.date}|${v.memberId}`,
        kind: 'entry',
        ts: v.date,
        label: `${m?.name || 'Someone'} logged ${fmtHours(v.hours)}`,
        detail: shortDate(v.date),
        drill: { kind: 'time-entry-list', weekStart: mondayOf(v.date) },
      });
    }
    // Invoice events
    for (const inv of invoices) {
      if (inv.sentDate) {
        out.push({
          id: `inv-sent|${inv.id}`,
          kind: 'invoice-sent',
          ts: inv.sentDate,
          label: `Invoice ${inv.invoiceNumber} sent`,
          detail: shortDate(inv.sentDate),
          drill: { kind: 'invoice', invoiceId: inv.id },
        });
      }
      if (inv.paidDate) {
        out.push({
          id: `inv-paid|${inv.id}`,
          kind: 'invoice-paid',
          ts: inv.paidDate,
          label: `Invoice ${inv.invoiceNumber} marked paid`,
          detail: shortDate(inv.paidDate),
          drill: { kind: 'invoice', invoiceId: inv.id },
        });
      }
    }
    // Deliverables completed/issued
    for (const d of deliverables) {
      if (d.status === 'complete' && d.updatedAt) {
        out.push({
          id: `del-c|${d.id}`,
          kind: 'deliverable-complete',
          ts: d.updatedAt.slice(0, 10),
          label: `${d.name} marked complete`,
          detail: shortDate(d.updatedAt.slice(0, 10)),
          drill: { kind: 'deliverable', deliverableId: d.id },
        });
      }
      if (d.issued && d.sealDate) {
        out.push({
          id: `del-i|${d.id}`,
          kind: 'deliverable-issued',
          ts: d.sealDate,
          label: `${d.name} issued`,
          detail: shortDate(d.sealDate),
          drill: { kind: 'deliverable', deliverableId: d.id },
        });
      }
    }
    // Sort newest first, cap to 30
    return out.sort((a, b) => b.ts.localeCompare(a.ts)).slice(0, 30);
  }, [entries, invoices, deliverables, ctx.members]);

  if (loading) return <WidgetSkeleton lines={3} />;
  if (items.length === 0) return <WidgetEmptyState text="No activity in the last 30 days." />;

  // Compact isn't offered for this widget (WidgetDef.sizes), but a stale
  // saved layout may still ask — render the standard list in that case.
  const visible = size === 'expanded' ? items : items.slice(0, 8);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      {size === 'expanded' && (
        <div style={statRow}>
          <div>
            <div style={bigNum}>{items.length}</div>
            <div style={subText}>events · last 30 days</div>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
        {visible.map((it) => {
          const icon = ACTIVITY_ICON[it.kind];
          return (
            <Row
              key={it.id}
              onClick={it.drill ? () => ctx.onDrill?.(it.drill!) : undefined}
              ariaLabel={it.label}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 'var(--radius-md)',
                  background: `color-mix(in srgb, ${icon.color} 15%, transparent)`,
                  color: icon.color,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <icon.Icon size={13} />
              </span>
              <span style={{ flex: 1, fontSize: 'var(--font-size-base)', color: 'var(--tx)' }}>
                {it.label}
              </span>
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--tm)' }}>
                {it.detail}
              </span>
            </Row>
          );
        })}
      </div>
    </div>
  );
}

const ACTIVITY_ICON: Record<ActivityItem['kind'], { Icon: React.ElementType; color: string }> = {
  entry: { Icon: Clock, color: COLOR_INFO },
  'invoice-sent': { Icon: Mail, color: COLOR_INFO },
  'invoice-paid': { Icon: Check, color: COLOR_OK },
  'deliverable-complete': { Icon: CheckCircle2, color: COLOR_OK },
  'deliverable-issued': { Icon: Star, color: COLOR_FORECAST },
};

// ═══════════════════════════════════════════════════════════════
// 12. PHASE HEALTH — current phase actuals vs phase budget
//    compact  = phase hours hero
//    standard = phase summary + elapsed
//    expanded = hours + $ (perm-gated) + phase actions
// ═══════════════════════════════════════════════════════════════

export function PhaseHealthWidget({ ctx, size }: { ctx: ProjectWidgetCtx; size: Size }) {
  const canSeeMoney = hasPermission(ctx.userPermissions, 'projects.view_financials');
  const [phases, setPhases] = useState<ProjectPhase[]>([]);
  const [budgets, setBudgets] = useState<ProjectBudget[]>([]);
  const [entries, setEntries] = useState<HourEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const [pR, bR, eR] = await Promise.allSettled([
        TimeService.syncPhasesWithDefaults(ctx.project.id),
        ProjectBudgetService.listForProject(ctx.project.id),
        TimeService.listEntries(ctx.project.id),
      ]);
      if (!mounted) return;
      if (pR.status === 'fulfilled') setPhases(pR.value);
      if (bR.status === 'fulfilled') setBudgets(bR.value);
      if (eR.status === 'fulfilled') setEntries(eR.value);
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, [ctx.project.id]);

  const currentPhase = phases.find((p) => p.id === ctx.project.currentPhaseId);
  const phaseEntries = currentPhase ? entries.filter((e) => e.phaseId === currentPhase.id) : [];
  const actualHours = phaseEntries.reduce((s, e) => s + (Number(e.hours) || 0), 0);
  const phaseBudget = currentPhase
    ? budgets.find((b) => b.phaseId === currentPhase.id && !b.billingRoleId)
    : null;
  const budgetHours = phaseBudget?.budgetHours || 0;
  const budgetAmount = phaseBudget?.budgetAmount || 0;
  const actualCost = phaseEntries.reduce((s, e) => {
    const m = ctx.members.find((x) => x.id === e.teamMemberId);
    return s + (Number(e.hours) || 0) * projectMemberRate(m, ctx.billingRoles);
  }, 0);
  const hourPct = budgetHours > 0 ? (actualHours / budgetHours) * 100 : 0;
  const dollarPct = budgetAmount > 0 ? (actualCost / budgetAmount) * 100 : 0;
  const color = hourPct > 100 ? COLOR_DANGER : hourPct > 90 ? COLOR_WARN : COLOR_OK;

  // Time spent vs phase span
  let timeSpentPct: number | null = null;
  if (currentPhase?.startDate && currentPhase?.endDate) {
    const total = daysBetween(currentPhase.startDate, currentPhase.endDate);
    const spent = daysBetween(currentPhase.startDate, todayIso());
    timeSpentPct = total > 0 ? (spent / total) * 100 : null;
  }

  if (loading) return <WidgetSkeleton lines={2} />;

  if (!currentPhase) {
    return (
      <WidgetEmptyState
        text="No current phase set."
        action="Open phases"
        onAction={() => ctx.onDrill?.({ kind: 'widget-focus', widgetId: 'phases' })}
      />
    );
  }

  if (size === 'compact') {
    return (
      <KpiTile
        value={fmtHours(actualHours)}
        label={`${currentPhase.name}${budgetHours > 0 ? ` · ${fmtPct(hourPct)} of budget` : ''}`}
        bar={budgetHours > 0 ? { pct: hourPct, color } : undefined}
        color={color}
        onClick={() => ctx.onDrill?.({ kind: 'phase', phaseId: currentPhase.id })}
        ariaLabel={`${fmtHours(actualHours)} logged in ${currentPhase.name}. View phase`}
      />
    );
  }

  if (size === 'standard') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)',
          padding: 'var(--space-1)',
        }}
      >
        <div style={statRow}>
          <div>
            <div style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: COLOR_INFO }}>
              {currentPhase.name}
            </div>
            <div style={subText}>current phase</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ ...bigNum, color }}>{fmtHours(actualHours)}</div>
            <div style={subText}>
              {budgetHours > 0 ? `of ${fmtHours(budgetHours)}` : 'no hour budget'}
            </div>
          </div>
        </div>
        {budgetHours > 0 && <ProgressBar pct={hourPct} color={color} />}
        {timeSpentPct !== null && (
          <div>
            <div style={{ ...subText, marginBottom: 'var(--space-1)' }}>
              Phase time elapsed: {fmtPct(timeSpentPct)}
            </div>
            <ProgressBar pct={timeSpentPct} color={COLOR_NEUTRAL} height={4} />
          </div>
        )}
      </div>
    );
  }

  // EXPANDED
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div>
        <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, color: COLOR_INFO }}>
          {currentPhase.name}
        </div>
        <div style={subText}>
          {currentPhase.startDate ? shortDate(currentPhase.startDate) : '—'} →{' '}
          {currentPhase.endDate ? shortDate(currentPhase.endDate) : '—'}
          {timeSpentPct !== null && ` · ${fmtPct(timeSpentPct)} of phase elapsed`}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
        <div>
          <div style={{ ...bigNum, color }}>
            {fmtHours(actualHours)}
            {budgetHours > 0 && (
              <span
                style={{ fontSize: 'var(--font-size-base)', color: 'var(--tm)', fontWeight: 400 }}
              >
                {' '}
                / {fmtHours(budgetHours)}
              </span>
            )}
          </div>
          <div style={subText}>
            {budgetHours > 0 ? `${fmtPct(hourPct)} of phase hours` : 'no phase hour budget'}
          </div>
          {budgetHours > 0 && <ProgressBar pct={hourPct} color={color} />}
        </div>
        {canSeeMoney && (
          <div>
            <div
              style={{
                ...bigNum,
                color: dollarPct > 100 ? COLOR_DANGER : dollarPct > 90 ? COLOR_WARN : COLOR_OK,
              }}
            >
              {fmtMoney(actualCost)}
              {budgetAmount > 0 && (
                <span
                  style={{
                    fontSize: 'var(--font-size-base)',
                    color: 'var(--tm)',
                    fontWeight: 400,
                  }}
                >
                  {' '}
                  / {fmtMoney(budgetAmount)}
                </span>
              )}
            </div>
            <div style={subText}>
              {budgetAmount > 0 ? `${fmtPct(dollarPct)} of phase budget` : 'no phase $ budget'}
            </div>
            {budgetAmount > 0 && (
              <ProgressBar
                pct={dollarPct}
                color={dollarPct > 100 ? COLOR_DANGER : dollarPct > 90 ? COLOR_WARN : COLOR_OK}
              />
            )}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
        <button
          onClick={() => ctx.onDrill?.({ kind: 'phase', phaseId: currentPhase.id })}
          style={btnGhost}
        >
          Open phase
        </button>
        <button
          onClick={() => ctx.onDrill?.({ kind: 'budget-editor', phaseId: currentPhase.id })}
          style={btnGhost}
        >
          Edit phase budget
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// 13. RISKS / OPEN ITEMS — project-linked tasks
//    compact  = urgent/open hero
//    standard = stats + top-6 by priority + add
//    expanded = 4 stats + full sorted list + add
// ═══════════════════════════════════════════════════════════════

export function RisksOpenItemsWidget({ ctx, size }: { ctx: ProjectWidgetCtx; size: Size }) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const all = await TaskService.listTasks();
      if (!mounted) return;
      setTasks(all.filter((t) => t.projectId === ctx.project.id));
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, [ctx.project.id]);

  const open = useMemo(() => tasks.filter((t) => t.status !== 'done'), [tasks]);
  const today = todayIso();
  const overdue = useMemo(
    () => open.filter((t) => (t as any).dueDate && (t as any).dueDate < today),
    [open, today]
  );
  const urgent = useMemo(() => open.filter((t) => t.priority === 'urgent'), [open]);
  const byPriority = useMemo(() => {
    const order: Record<Task['priority'], number> = { urgent: 0, high: 1, medium: 2, low: 3 };
    return [...open].sort(
      (a, b) =>
        (order[a.priority] || 9) - (order[b.priority] || 9) ||
        ((a as any).dueDate || '9999').localeCompare((b as any).dueDate || '9999')
    );
  }, [open]);

  const PRIORITY_COLOR: Record<Task['priority'], string> = {
    urgent: COLOR_DANGER,
    high: COLOR_WARN,
    medium: COLOR_INFO,
    low: COLOR_NEUTRAL,
  };

  if (loading) return <WidgetSkeleton lines={3} />;

  if (size === 'compact') {
    if (urgent.length > 0) {
      return (
        <KpiTile
          value={String(urgent.length)}
          label="urgent open"
          color={COLOR_DANGER}
          onClick={() => ctx.onDrill?.({ kind: 'task', taskId: urgent[0].id })}
          ariaLabel={`${urgent.length} urgent open items. View`}
        />
      );
    }
    return (
      <KpiTile
        value={String(open.length)}
        label={overdue.length > 0 ? `open · ${overdue.length} overdue` : 'open items'}
        color={overdue.length > 0 ? COLOR_WARN : 'var(--tx)'}
        onClick={
          byPriority[0]
            ? () => ctx.onDrill?.({ kind: 'task', taskId: byPriority[0].id })
            : undefined
        }
        ariaLabel={`${open.length} open items. View`}
      />
    );
  }

  if (size === 'standard') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)',
          padding: 'var(--space-1)',
        }}
      >
        <div style={statRow}>
          <div>
            <div style={bigNum}>{open.length}</div>
            <div style={subText}>open items</div>
          </div>
          <div>
            <div style={{ ...bigNum, color: overdue.length > 0 ? COLOR_DANGER : 'var(--tx)' }}>
              {overdue.length}
            </div>
            <div style={subText}>overdue</div>
          </div>
          <div>
            <div style={{ ...bigNum, color: urgent.length > 0 ? COLOR_DANGER : 'var(--tx)' }}>
              {urgent.length}
            </div>
            <div style={subText}>urgent</div>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-1)',
            maxHeight: 160,
            overflow: 'auto',
          }}
        >
          {byPriority.slice(0, 6).map((t) => {
            const due = (t as any).dueDate as string | undefined;
            const isOverdue = due && due < today;
            return (
              <Row
                key={t.id}
                onClick={() => ctx.onDrill?.({ kind: 'task', taskId: t.id })}
                ariaLabel={`Open item ${t.title}`}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 'var(--radius-full)',
                    background: PRIORITY_COLOR[t.priority],
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    flex: 1,
                    fontSize: 'var(--font-size-base)',
                    color: 'var(--tx)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t.title}
                </span>
                <span
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    color: isOverdue ? COLOR_DANGER : 'var(--tm)',
                    fontWeight: isOverdue ? 700 : 400,
                  }}
                >
                  {due ? shortDate(due) : ''}
                </span>
              </Row>
            );
          })}
        </div>
        <div style={{ textAlign: 'right' }}>
          <button onClick={() => ctx.onDrill?.({ kind: 'new-task' })} style={linkBtn}>
            + Add open item
          </button>
        </div>
      </div>
    );
  }

  // EXPANDED
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div style={statRow}>
        <div>
          <div style={bigNum}>{open.length}</div>
          <div style={subText}>open</div>
        </div>
        <div>
          <div style={{ ...bigNum, color: COLOR_DANGER }}>{urgent.length}</div>
          <div style={subText}>urgent</div>
        </div>
        <div>
          <div style={{ ...bigNum, color: COLOR_WARN }}>{overdue.length}</div>
          <div style={subText}>overdue</div>
        </div>
        <div>
          <div style={{ ...bigNum, color: COLOR_OK }}>
            {tasks.filter((t) => t.status === 'done').length}
          </div>
          <div style={subText}>done</div>
        </div>
      </div>
      <div>
        <div style={sectionLabel}>All open · sorted by priority + due</div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-1)',
            maxHeight: 280,
            overflow: 'auto',
          }}
        >
          {byPriority.length === 0 && (
            <div style={{ ...subText, padding: 'var(--space-2)' }}>
              No open items. Smooth sailing.
            </div>
          )}
          {byPriority.map((t) => {
            const due = (t as any).dueDate as string | undefined;
            const isOverdue = due && due < today;
            return (
              <Row
                key={t.id}
                onClick={() => ctx.onDrill?.({ kind: 'task', taskId: t.id })}
                ariaLabel={`Open item ${t.title}, ${t.priority} priority`}
              >
                <span
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 700,
                    padding: '2px var(--space-2)',
                    borderRadius: 'var(--radius-sm)',
                    background: `color-mix(in srgb, ${PRIORITY_COLOR[t.priority]} 18%, transparent)`,
                    color: PRIORITY_COLOR[t.priority],
                    textTransform: 'uppercase',
                    width: 60,
                    textAlign: 'center',
                    flexShrink: 0,
                  }}
                >
                  {t.priority}
                </span>
                <span style={{ flex: 1, fontSize: 'var(--font-size-base)', color: 'var(--tx)' }}>
                  {t.title}
                </span>
                <span
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--tm)',
                    width: 70,
                    textAlign: 'center',
                    fontWeight: 500,
                  }}
                >
                  {t.status}
                </span>
                <span
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    color: isOverdue ? COLOR_DANGER : 'var(--tm)',
                    fontWeight: isOverdue ? 700 : 400,
                    width: 90,
                    textAlign: 'right',
                  }}
                >
                  {due ? shortDate(due) : '—'}
                </span>
              </Row>
            );
          })}
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={() => ctx.onDrill?.({ kind: 'new-task' })} style={btnGhost}>
          + Add open item
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// DASHBOARD VIEWS BAR — saved-view chips + actions
// ═══════════════════════════════════════════════════════════════

export function DashboardViewsBar({
  views,
  activeViewId,
  currentUserId,
  onSwitch,
  onSaveAs,
  onSetDefault,
  onDelete,
}: {
  views: DashboardView[];
  activeViewId: string | null;
  currentUserId: string;
  onSwitch: (id: string | null) => void;
  onSaveAs: () => void;
  onSetDefault: () => void;
  onDelete: () => void;
}) {
  const ownViews = views.filter((v) => v.userId === currentUserId);
  const sharedViews = views.filter((v) => v.userId !== currentUserId && v.shared);
  const activeView = views.find((v) => v.id === activeViewId);
  const isOwnerOfActive = activeView && activeView.userId === currentUserId;

  const chip = (active: boolean, accent: string): React.CSSProperties => ({
    padding: 'var(--space-1) var(--space-2)',
    borderRadius: 'var(--radius-full)',
    border: `1px solid ${active ? accent : 'var(--bd)'}`,
    background: active ? `color-mix(in srgb, ${accent} 10%, transparent)` : 'transparent',
    color: active ? accent : 'var(--tx)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: active ? 700 : 500,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--space-2)',
  });

  return (
    <div
      className="dashboard-views-bar"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        marginBottom: 'var(--space-3)',
        padding: 'var(--space-2) var(--space-3)',
        background: 'var(--sf)',
        border: '1px solid var(--bd)',
        borderRadius: 'var(--radius-lg)',
        flexWrap: 'wrap',
      }}
    >
      <span style={sectionLabelInline}>View</span>
      <button
        onClick={() => onSwitch(null)}
        style={{
          ...chip(activeViewId === null, 'var(--ac)'),
          color: activeViewId === null ? 'var(--ac)' : 'var(--tm)',
        }}
      >
        Default
      </button>
      {ownViews.map((v) => {
        const active = v.id === activeViewId;
        return (
          <button key={v.id} onClick={() => onSwitch(v.id)} style={chip(active, 'var(--ac)')}>
            {v.name}
            {v.isDefault && (
              <span style={{ display: 'inline-flex', color: COLOR_OK }} title="Your default view">
                <Star size={11} aria-hidden="true" />
              </span>
            )}
            {v.shared && (
              <span
                style={{ display: 'inline-flex', color: COLOR_INFO }}
                title="Shared with the firm"
              >
                <ArrowLeftRight size={11} aria-hidden="true" />
              </span>
            )}
          </button>
        );
      })}
      {sharedViews.length > 0 && (
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--tm)',
            paddingLeft: 'var(--space-2)',
            borderLeft: '1px solid var(--bd)',
            marginLeft: 'var(--space-1)',
          }}
        >
          Firm shared
        </span>
      )}
      {sharedViews.map((v) => {
        const active = v.id === activeViewId;
        return (
          <button
            key={v.id}
            onClick={() => onSwitch(v.id)}
            title="Shared view (read-only)"
            style={chip(active, COLOR_INFO)}
          >
            <ArrowLeftRight size={11} aria-hidden="true" /> {v.name}
          </button>
        );
      })}
      <div style={{ flex: 1 }} />
      {isOwnerOfActive && (
        <>
          <button
            onClick={onSetDefault}
            style={{
              ...tinyBtn,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-1)',
            }}
            title="Make this view your default"
          >
            <Star size={11} aria-hidden="true" /> Default
          </button>
          <button onClick={onDelete} style={{ ...tinyBtn, color: COLOR_DANGER }}>
            Delete
          </button>
        </>
      )}
      <button onClick={onSaveAs} style={tinyBtnPrimary}>
        + Save current as view
      </button>
    </div>
  );
}

const tinyBtn: React.CSSProperties = {
  padding: 'var(--space-1) var(--space-2)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--bd)',
  background: 'transparent',
  color: 'var(--tm)',
  fontSize: 'var(--font-size-sm)',
  cursor: 'pointer',
};

const tinyBtnPrimary: React.CSSProperties = {
  padding: 'var(--space-1) var(--space-3)',
  borderRadius: 'var(--radius-md)',
  border: 'none',
  background: 'var(--ac)',
  color: 'var(--color-text-inverse)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  cursor: 'pointer',
};

// ─── Shared local styles ────────────────────────────────────────────

const sectionLabel: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 700,
  color: 'var(--tm)',
  letterSpacing: '.08em',
  textTransform: 'uppercase',
  marginBottom: 'var(--space-2)',
};

const sectionLabelInline: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 700,
  color: 'var(--tm)',
  letterSpacing: '.08em',
  textTransform: 'uppercase',
};

const inputStyle: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-3)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--bd)',
  background: 'var(--ib)',
  color: 'var(--tx)',
  fontSize: 'var(--font-size-base)',
  fontFamily: 'var(--font-mono)',
};

const btnGhost: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-3)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--bd)',
  background: 'transparent',
  color: 'var(--tm)',
  fontSize: 'var(--font-size-sm)',
  cursor: 'pointer',
};

function btnPrimary(disabled: boolean): React.CSSProperties {
  return {
    padding: 'var(--space-2) var(--space-3)',
    borderRadius: 'var(--radius-md)',
    border: 'none',
    background: 'var(--ac)',
    color: 'var(--color-text-inverse)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
  };
}

const linkBtn: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'var(--ac)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  cursor: 'pointer',
  padding: 0,
};

/** Info/context callout — left-accent strip on a subtle surface. */
function calloutStyle(accent: string): React.CSSProperties {
  return {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--tm)',
    padding: 'var(--space-2) var(--space-3)',
    background: 'var(--sa)',
    borderRadius: 'var(--radius-md)',
    borderLeft: `3px solid ${accent}`,
  };
}

function miniBox(color: string): React.CSSProperties {
  return {
    padding: 'var(--space-2) var(--space-3)',
    borderRadius: 'var(--radius-md)',
    background: `color-mix(in srgb, ${color} 10%, transparent)`,
    border: `1px solid color-mix(in srgb, ${color} 30%, transparent)`,
  };
}

const miniLabel: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--tm)',
  fontWeight: 500,
  marginTop: 2,
};
